// src/components/DocWriterMath.jsx
// UPDATED 2026-08-12 v12: editable labels, supplemental title/note lines, and prose-safe math source rendering.
// The Math ownership/model/rendering contract remains unchanged. This revision only adds:
// editable subject/description text, repeatable top-title/bottom-note lines with Table-style
// formatting, and plain-text line rendering so ordinary alphanumeric prose keeps its spaces.
// Existing drag/resize, Undo/Redo, delete confirmation, Canvas handoff, save/snapshot, and
// presentation controls remain unchanged.

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { BlockMath } from "react-katex";
import "katex/dist/katex.min.css";

const TEXT_DARK = "#0f172a";
const TEXT_MUTED = "#475569";
const BORDER = "rgba(15, 23, 42, 0.10)";
const SOFT_BG = "#f8fafc";
const PANEL_BG = "#ffffff";
const ACCENT = "#1d4ed8";
const ACCENT_SOFT = "rgba(29, 78, 216, 0.10)";
const SHADOW = "0 14px 34px rgba(15, 23, 42, 0.08)";

const SIZE_CONFIG = {
  small: {
    label: "Small",
    fontSize: 0.94,
    paddingY: 14,
    paddingX: 16,
    lineGap: 8,
  },
  medium: {
    label: "Medium",
    fontSize: 1.0,
    paddingY: 18,
    paddingX: 20,
    lineGap: 10,
  },
  large: {
    label: "Large",
    fontSize: 1.08,
    paddingY: 22,
    paddingX: 24,
    lineGap: 12,
  },
};

const WIDTH_MODES = {
  content: { label: "Content width", width: "fit-content", maxWidth: "100%" },
  medium: { label: "Medium", width: "72%", maxWidth: "100%" },
  wide: { label: "Wide", width: "88%", maxWidth: "100%" },
  full: { label: "Full width", width: "100%", maxWidth: "100%" },
};

const SCREEN_VARIANTS = {
  plain: {
    label: "Plain",
    background: "transparent",
    border: "transparent",
    accent: "transparent",
  },
  soft: {
    label: "Soft",
    background: SOFT_BG,
    border: BORDER,
    accent: ACCENT_SOFT,
  },
  academic: {
    label: "Academic",
    background: "#fffef8",
    border: "rgba(161, 98, 7, 0.16)",
    accent: "rgba(161, 98, 7, 0.14)",
  },
};

const FINANCE_TERMS = [
  "roi",
  "gross margin",
  "net margin",
  "profit margin",
  "markup",
  "discount",
  "interest",
  "compound",
  "present value",
  "future value",
  "pv",
  "fv",
  "npv",
  "irr",
  "ebitda",
  "depreciation",
  "amortization",
  "basis",
  "tax",
  "rate",
  "revenue",
  "cogs",
  "cash flow",
  "break even",
  "breakeven",
  "assets",
  "liabilities",
  "equity",
  "debit",
  "credit",
  "working capital",
  "current ratio",
  "quick ratio",
  "debt-to-equity",
  "return on assets",
  "gross profit",
  "net operating profit",
];

const CALCULUS_TERMS = [
  "d/dx",
  "dy/dx",
  "derivative",
  "integral",
  "\\int",
  "∫",
  "lim",
  "limit",
  "dx",
  "∂",
  "partial",
  "gradient",
  "nabla",
  "antiderivative",
  "definite integral",
  "instantaneous rate",
  "rate of change",
];

const CHEMISTRY_TERMS = [
  "molar",
  "mole",
  "reaction",
  "equilibrium",
  "acid",
  "base",
  "ph",
  "h2o",
  "co2",
  "o2",
  "h2",
  "n2",
  "nh3",
  "nacl",
  "mol",
  "stoichiometry",
  "kc",
  "kp",
  "delta g",
  "gibbs",
  "le chatelier",
  "le châtelier",
];

const PHYSICS_TERMS = [
  "force",
  "velocity",
  "acceleration",
  "energy",
  "momentum",
  "gravity",
  "mass",
  "newton",
  "kinetic",
  "potential",
  "voltage",
  "current",
  "resistance",
  "ohm",
  "coulomb",
  "frequency",
  "wavelength",
  "pressure",
  "density",
  "pv=nrt",
  "e=mc",
  "f=ma",
];

const STATS_TERMS = [
  "probability",
  "variance",
  "standard deviation",
  "mean",
  "median",
  "mode",
  "z-score",
  "z score",
  "confidence interval",
  "normal distribution",
  "bayes",
  "expected value",
  "p(",
  "σ",
  "mu",
  "regression",
  "correlation",
  "r²",
  "r^2",
];

const GREEK_LATEX_MAP = [
  [/\balpha\b/gi, "\\alpha"],
  [/\bbeta\b/gi, "\\beta"],
  [/\bgamma\b/gi, "\\gamma"],
  [/\bdelta\b/gi, "\\delta"],
  [/\bDelta\b/g, "\\Delta"],
  [/\btheta\b/gi, "\\theta"],
  [/\blambda\b/gi, "\\lambda"],
  [/\bmu\b/gi, "\\mu"],
  [/\bsigma\b/gi, "\\sigma"],
  [/\bSigma\b/g, "\\Sigma"],
  [/\bomega\b/gi, "\\omega"],
  [/\bOmega\b/g, "\\Omega"],
  [/\bpi\b/gi, "\\pi"],
  [/\brho\b/gi, "\\rho"],
  [/\bphi\b/gi, "\\phi"],
];

const LATEX_SYMBOL_MAP = [
  [/<=/g, "\\le"],
  [/>=/g, "\\ge"],
  [/!=/g, "\\ne"],
  [/\+\/-/g, "\\pm"],
  [/<=>/g, "\\rightleftharpoons"],
  [/<->/g, "\\rightleftharpoons"],
  [/-->/g, "\\to"],
  [/->/g, "\\to"],
  [/<-/g, "\\leftarrow"],
  [/≈/g, "\\approx"],
  [/≤/g, "\\le"],
  [/≥/g, "\\ge"],
  [/≠/g, "\\ne"],
  [/±/g, "\\pm"],
  [/∞/g, "\\infty"],
  [/∫/g, "\\int"],
  [/∂/g, "\\partial"],
  [/∑/g, "\\sum"],
  [/×/g, "\\times"],
  [/÷/g, "\\div"],
];

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function safeJsonParse(value) {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function stripOuterMarker(value) {
  return String(value || "")
    .replace(
      /^\s*\[(math|formula|equation|chemistry|finance|accounting|calculation|physics|science|statistics|probability)\]\s*/i,
      ""
    )
    .trim();
}

function stripLatexDelimiters(value) {
  return String(value || "")
    .replace(/^\s*\$\$/g, "")
    .replace(/\$\$\s*$/g, "")
    .replace(/^\s*\\\[/g, "")
    .replace(/\\\]\s*$/g, "")
    .replace(/^\s*\\\(/g, "")
    .replace(/\\\)\s*$/g, "")
    .trim();
}

function normalizeContent(value) {
  let out = stripOuterMarker(value)
    .replace(/\r\n/g, "\n")
    .replace(/\t/g, "  ")
    .trim();

  out = out.replace(/^\s*\\\[\s*\n?([\s\S]*?)\n?\s*\\\]\s*$/m, "$1").trim();
  out = out.replace(/^\s*\$\$\s*\n?([\s\S]*?)\n?\s*\$\$\s*$/m, "$1").trim();
  out = out.replace(/^\s*\\\(([\s\S]*?)\\\)\s*$/m, "$1").trim();

  return out;
}

const DISPLAY_LINE_BREAK_ENVIRONMENTS = new Set([
  "aligned",
  "alignedat",
  "gathered",
  "array",
  "matrix",
  "pmatrix",
  "bmatrix",
  "bmatrix",
  "vmatrix",
  "vmatrix",
  "cases",
  "smallmatrix",
  "split",
]);

function hasDisplayLineBreakEnvironment(value) {
  const source = String(value || "");
  const pattern = /\\begin\s*\{([a-zA-Z*]+)\}/g;
  let match = pattern.exec(source);

  while (match) {
    const name = String(match[1] || "")
      .replace(/\*$/, "")
      .toLowerCase();
    if (DISPLAY_LINE_BREAK_ENVIRONMENTS.has(name)) return true;
    match = pattern.exec(source);
  }

  return false;
}

function splitStandaloneDisplayBreaks(value) {
  const source = String(value || "").trim();
  if (!source) return [];
  if (hasDisplayLineBreakEnvironment(source)) return [source];

  // Outside a real multiline environment, KaTeX warns because LaTeX-style
  // display line-break commands (\\ or \newline) do nothing. In Doc Writer,
  // those commands mean "start the next displayed equation line", so convert
  // them into the same explicit line model used by ordinary source newlines.
  return source
    .split(/\\{2,}\*?(?:\s*\[[^\]]*\])?|\\newline\b/g)
    .map((part) => String(part || "").trim())
    .filter(Boolean);
}

function splitMathLines(content) {
  const lines = String(content || "")
    .replace(/\r\n/g, "\n")
    .split(/\n/);

  const out = [];
  let displayCollecting = false;
  let environmentCollecting = false;
  let environmentStack = [];
  let buffer = [];

  const environmentTokens = (value) => {
    const tokens = [];
    const pattern = /\\(begin|end)\s*\{([a-zA-Z*]+)\}/g;
    let match = pattern.exec(String(value || ""));
    while (match) {
      tokens.push({
        kind: match[1],
        name: String(match[2] || "").replace(/\*$/, "").toLowerCase(),
      });
      match = pattern.exec(String(value || ""));
    }
    return tokens;
  };

  const applyEnvironmentTokens = (value) => {
    environmentTokens(value).forEach((token) => {
      if (token.kind === "begin") {
        environmentStack.push(token.name);
        return;
      }

      const lastIndex = environmentStack.lastIndexOf(token.name);
      if (lastIndex >= 0) {
        environmentStack.splice(lastIndex, 1);
      } else if (environmentStack.length) {
        environmentStack.pop();
      }
    });
  };

  function flushBuffer() {
    const value = buffer.join("\n").trim();
    if (value) {
      splitStandaloneDisplayBreaks(value).forEach((part) => out.push(part));
    }
    buffer = [];
  }

  for (const rawLine of lines) {
    const sourceLine = String(rawLine || "").replace(/\t/g, "  ");
    const line = sourceLine.trim();

    if (displayCollecting) {
      if (/^\s*(?:\\\]|\$\$)\s*$/.test(line)) {
        displayCollecting = false;
        flushBuffer();
      } else {
        buffer.push(line);
      }
      continue;
    }

    if (environmentCollecting) {
      buffer.push(line);
      applyEnvironmentTokens(line);
      if (environmentStack.length === 0) {
        environmentCollecting = false;
        flushBuffer();
      }
      continue;
    }

    if (!line) continue;

    const oneLineDisplay = line.match(
      /^\s*(?:\\\[([\s\S]+?)\\\]|\$\$([\s\S]+?)\$\$)\s*$/
    );
    if (oneLineDisplay) {
      const value = String(oneLineDisplay[1] || oneLineDisplay[2] || "").trim();
      if (value) {
        splitStandaloneDisplayBreaks(value).forEach((part) => out.push(part));
      }
      continue;
    }

    const oneLineParen = line.match(/^\s*\\\(([\s\S]+?)\\\)\s*$/);
    if (oneLineParen) {
      const value = String(oneLineParen[1] || "").trim();
      if (value) {
        splitStandaloneDisplayBreaks(value).forEach((part) => out.push(part));
      }
      continue;
    }

    if (/^\s*(?:\\\[|\$\$)\s*$/.test(line)) {
      displayCollecting = true;
      buffer = [];
      continue;
    }

    const tokens = environmentTokens(line);
    if (tokens.some((token) => token.kind === "begin")) {
      environmentCollecting = true;
      environmentStack = [];
      buffer = [line];
      applyEnvironmentTokens(line);
      if (environmentStack.length === 0) {
        environmentCollecting = false;
        flushBuffer();
      }
      continue;
    }

    // Outside multiline environments, normalize display-mode line-break commands
    // into Doc Writer's explicit line model instead of passing them to BlockMath.
    // This preserves the visual line break and removes KaTeX's newLineInDisplayMode warning.
    const leadingIndent = (sourceLine.match(/^ */) || [""])[0];
    const cleanBody = stripLatexDelimiters(line).trim();
    splitStandaloneDisplayBreaks(cleanBody).forEach((part, index) => {
      const clean = part
        ? `${index === 0 ? leadingIndent : ""}${part}`
        : "";
      if (clean) out.push(clean);
    });
  }

  if (buffer.length) flushBuffer();

  return out.filter(Boolean);
}

function escapeLatexText(value) {
  return String(value || "")
    .replace(/\\/g, "\\textbackslash ")
    .replace(/&/g, "\\&")
    .replace(/%/g, "\\%")
    .replace(/#/g, "\\#")
    .replace(/\$/g, "\\$")
    .replace(/_/g, "\\_");
}

function isLikelyLatex(line) {
  return /\\[a-zA-Z]+|\\frac|\\sqrt|\\int|\\sum|\\lim|\\left|\\right|_\{|_\w|\^\{|[{}]/.test(
    line
  );
}

function hasMostlyWords(value) {
  const letters = (String(value).match(/[A-Za-z]/g) || []).length;
  const operators = (String(value).match(/[=+\-*/()]/g) || []).length;
  return letters > 12 && operators >= 1;
}

function wrapWordsInText(value) {
  return String(value || "")
    .trim()
    .split(/(\s*[+\-]\s*)/)
    .map((part) => {
      if (/^\s*[+\-]\s*$/.test(part)) return part.trim() === "+" ? " + " : " - ";
      const clean = part.trim();
      if (!clean) return "";
      if (/^\\/.test(clean)) return clean;
      if (/^[A-Z]{2,}$/.test(clean)) return `\\text{${escapeLatexText(clean)}}`;
      if (/[A-Za-z]/.test(clean)) return `\\text{${escapeLatexText(clean)}}`;
      return clean;
    })
    .join("");
}

function wrapWordsAroundOperators(value) {
  return String(value || "")
    .split(/(=|\\times|\+|\-)/)
    .map((part) => {
      const trimmed = part.trim();
      if (!trimmed) return part;
      if (/^(=|\\times|\+|\-)$/.test(trimmed)) return ` ${trimmed} `;
      if (/\\text\{|\\frac\{|\\sqrt\{|\\[a-zA-Z]+/.test(trimmed)) return trimmed;
      if (/[A-Za-z][A-Za-z\s.-]+/.test(trimmed) && !/[0-9][A-Za-z]/.test(trimmed)) {
        return `\\text{${escapeLatexText(trimmed)}}`;
      }
      return trimmed;
    })
    .join("")
    .replace(/\s{2,}/g, " ")
    .trim();
}

function convertWordFormulaToLatex(value) {
  let out = String(value || "");

  out = out.replace(/\(([^()]+)\)\s*\/\s*([A-Za-z][A-Za-z\s.-]*)/g, (_, numerator, denominator) => {
    return `\\frac{${wrapWordsInText(numerator)}}{${wrapWordsInText(denominator)}}`;
  });

  out = out.replace(/([A-Za-z][A-Za-z\s.-]*?)\s*\/\s*([A-Za-z][A-Za-z\s.-]*)/g, (_, numerator, denominator) => {
    if (numerator.includes("\\") || denominator.includes("\\")) return `${numerator}/${denominator}`;
    return `\\frac{${wrapWordsInText(numerator)}}{${wrapWordsInText(denominator)}}`;
  });

  out = wrapWordsAroundOperators(out);
  return out;
}

function convertChemicalFormulasToLatex(value) {
  let out = String(value || "");
  out = out.replace(/([A-Z][a-z]?|\))(\d+)/g, (_, atom, digits) => `${atom}_{${digits}}`);
  out = out.replace(/\^\{?(\d*[+-])\}?/g, "^{$1}");
  return out;
}

function normalizePlainToLatex(line) {
  const sourceLine = String(line || "").replace(/\t/g, "  ");
  const leadingSpaces = (sourceLine.match(/^ */) || [""])[0].length;
  let out = stripLatexDelimiters(sourceLine)
    .replace(/\*\*/g, "")
    .replace(/__/g, "")
    .trim();

  if (!out) return "";

  const alreadyLatex = isLikelyLatex(out);

  for (const [pattern, replacement] of GREEK_LATEX_MAP) {
    out = out.replace(pattern, replacement);
  }

  for (const [pattern, replacement] of LATEX_SYMBOL_MAP) {
    out = out.replace(pattern, replacement);
  }

  out = out.replace(/\bsqrt\s*\(([^()]*)\)/gi, "\\sqrt{$1}");
  out = out.replace(/\binfinity\b/gi, "\\infty");
  out = out.replace(/\binf\b/gi, "\\infty");
  out = out.replace(/\btimes\b/gi, "\\times");
  out = out.replace(/\bdivided by\b/gi, "\\div");
  out = out.replace(/\bDelta\s*G\b/gi, "\\Delta G");
  out = out.replace(/\bd\/dx\b/gi, "\\frac{d}{dx}");
  out = out.replace(/\bdy\/dx\b/gi, "\\frac{dy}{dx}");
  out = out.replace(/\blim\s+([a-zA-Z])\s*\\to\s*([^\s]+)\s+/gi, "\\lim_{$1 \\to $2} ");
  out = out.replace(/\s+\*\s+/g, " \\times ");
  out = convertChemicalFormulasToLatex(out);
  out = out.replace(/\^(-?\d+|[a-zA-Z])\b/g, "^{$1}");
  out = out.replace(/\^\(([^)]+)\)/g, "^{$1}");
  out = out.replace(/_([A-Za-z0-9]+)\b/g, "_{$1}");

  if (!alreadyLatex && hasMostlyWords(out)) {
    out = convertWordFormulaToLatex(out);
  }

  // Leading spaces entered in the draft source act as a simple per-line indent.
  // KaTeX normally collapses ordinary whitespace, so translate only the leading
  // indentation into a bounded horizontal space while leaving the equation intact.
  if (leadingSpaces > 0) {
    const indentEm = Math.min(20, leadingSpaces) * 0.35;
    out = `\\hspace{${Number(indentEm.toFixed(2))}em}${out}`;
  }

  return out.trim();
}

function detectMathKind(content) {
  const lower = String(content || "").toLowerCase();

  if (CHEMISTRY_TERMS.some((term) => lower.includes(term))) return "Chemistry / Reaction";
  if (PHYSICS_TERMS.some((term) => lower.includes(term))) return "Physics Formula";
  if (FINANCE_TERMS.some((term) => lower.includes(term))) return "Finance / Accounting Formula";
  if (CALCULUS_TERMS.some((term) => lower.includes(term))) return "Calculus";
  if (STATS_TERMS.some((term) => lower.includes(term))) return "Statistics / Probability";
  if (/[a-z]\s*=\s*[-+*/^().0-9a-z\s]+/i.test(content)) return "Formula";
  if (/\d+\s*[+\-×*/÷]\s*\d+/.test(content)) return "Calculation";

  return "Math";
}

function buildReadableSummary(kind, content) {
  const lower = String(content || "").toLowerCase();

  if (kind.includes("Chemistry")) {
    if (/rightleftharpoons|⇌|<->/.test(content)) return "Equilibrium reaction / chemistry notation";
    if (/to|→|->/.test(content)) return "Chemical reaction / stoichiometry notation";
    return "Chemistry notation";
  }

  if (kind.includes("Finance")) {
    if (lower.includes("assets") && lower.includes("liabilities")) return "Accounting equation";
    if (lower.includes("roi")) return "Return on investment formula";
    if (lower.includes("present value") || /\bpv\b/.test(lower)) return "Time value of money formula";
    return "Finance/accounting formula";
  }

  if (kind.includes("Physics")) return "Physics/science formula";
  if (kind.includes("Calculus")) return "Calculus expression";
  if (kind.includes("Statistics")) return "Statistics/probability expression";

  return "";
}

function getLineClass(line) {
  const value = String(line || "");
  if (/^(therefore|∴|answer|result|final|conclusion)\b/i.test(value)) return "result";
  if (/^\s*(given|where|assume|let|step\s*\d+|solution|formula)\b/i.test(value)) return "note";
  if (/\\rightleftharpoons|\\to|→|⇌/.test(value)) return "reaction";
  if (/=|\\approx|\\le|\\ge|\\ne/.test(value)) return "equation";
  return "normal";
}

function tokenizeFallback(text) {
  const parts = [];
  const source = String(text || "");
  const re =
    /(\\frac\{[^}]*\}\{[^}]*\}|\\sqrt\{[^}]*\}|\\[a-zA-Z]+|\b\d+(?:\.\d+)?(?:e[-+]?\d+)?%?\b|[A-Za-z][A-Za-z0-9_\-]*|\+|\-|×|\\times|\/|÷|\\div|=|≈|≤|≥|≠|±|→|←|⇌|↔|∫|∂|∇|∑|Σ|π|∞|Δ|δ|θ|α|β|γ|λ|μ|σ|ω|Ω|ρ|φ|\(|\)|\[|\]|\{|\}|,|;|:)/g;

  let last = 0;
  let match;

  while ((match = re.exec(source)) !== null) {
    if (match.index > last) {
      parts.push({ type: "text", value: source.slice(last, match.index) });
    }

    const token = match[0];
    let type = "symbol";

    if (/^\d/.test(token)) type = "number";
    else if (/^[A-Za-z]/.test(token)) type = "identifier";
    else if (/^\\[a-zA-Z]+/.test(token)) type = "symbol";
    else if (/^[+\-×/÷=≈≤≥≠±→←⇌↔]$/.test(token) || /^\\times|\\div$/.test(token)) type = "operator";
    else if (/^[()[\]{},;:]$/.test(token)) type = "punctuation";

    parts.push({ type, value: token });
    last = match.index + token.length;
  }

  if (last < source.length) {
    parts.push({ type: "text", value: source.slice(last) });
  }

  return parts;
}

function FallbackLine({ line, index, alignment = "center" }) {
  const tokens = tokenizeFallback(line);
  const lineKind = getLineClass(line);

  return (
    <div
      className={`docwriter-math-line docwriter-math-line-${lineKind}`}
      key={`math-fallback-line-${index}`}
      style={{
        width: "100%",
        minWidth: 0,
        boxSizing: "border-box",
        textAlign: alignment,
        whiteSpace: "nowrap",
        overflowX: "auto",
        overflowY: "visible",
      }}
    >
      {tokens.map((token, tokenIndex) => (
        <span
          className={`docwriter-math-token docwriter-math-token-${token.type}`}
          key={`math-fallback-token-${index}-${tokenIndex}`}
        >
          {token.value}
        </span>
      ))}
    </div>
  );
}

function SafeBlockMath({ latex, original, index, alignment = "center" }) {
  const justifyContent =
    alignment === "left" ? "flex-start" : alignment === "right" ? "flex-end" : "center";

  try {
    return (
      <div
        className={`docwriter-math-line docwriter-math-line-${getLineClass(latex)}`}
        key={`math-katex-line-${index}`}
        style={{
          width: "100%",
          minWidth: 0,
          boxSizing: "border-box",
          display: "flex",
          justifyContent,
          alignItems: "center",
          overflowX: "auto",
          overflowY: "visible",
          whiteSpace: "nowrap",
          padding: "2px 0",
        }}
      >
        <div
          style={{
            flex: "0 0 auto",
            width: "max-content",
            maxWidth: "none",
            minWidth: 0,
            whiteSpace: "nowrap",
          }}
        >
          <BlockMath math={latex} errorColor="#dc2626" />
        </div>
      </div>
    );
  } catch {
    return <FallbackLine line={original || latex} index={index} alignment={alignment} />;
  }
}

const DOC_WRITER_MATH_COMPONENT_VERSION = "2026.08.12-v12";
const DOC_WRITER_MATH_LAYOUT_VERSION = 9;
const DOC_WRITER_MATH_EDITOR_ONLY_ATTR = "data-doc-editor-only";

// Deliberately tiny safety floor: the user may shrink a Math object almost to a dot,
// but never to an unrecoverable 0 × 0 object.
const DOC_WRITER_MATH_MIN_WIDTH_PX = 16;
const DOC_WRITER_MATH_MAX_WIDTH_PX = 1600;
const DOC_WRITER_MATH_MIN_HEIGHT_PX = 16;
const DOC_WRITER_MATH_MAX_HEIGHT_PX = 1200;
const DOC_WRITER_MATH_RESIZE_HANDLE_PX = 24;

function finiteNumber(value, fallback = 0) {
  const parsed = Number.parseFloat(String(value ?? ""));
  return Number.isFinite(parsed) ? parsed : fallback;
}

function normalizeAlignment(value, fallback = "center") {
  const normalized = String(value || "").trim().toLowerCase();
  return ["left", "center", "right"].includes(normalized) ? normalized : fallback;
}

function normalizeScreenVariant(value) {
  const normalized = String(value || "").trim().toLowerCase();
  return SCREEN_VARIANTS[normalized] ? normalized : "soft";
}

function normalizeSizePreset(value) {
  const normalized = String(value || "").trim().toLowerCase();
  return SIZE_CONFIG[normalized] ? normalized : "medium";
}

function normalizeWidthMode(value) {
  const normalized = String(value || "").trim().toLowerCase();
  return WIDTH_MODES[normalized] || normalized === "custom" ? normalized : "content";
}

function normalizeColorValue(value, fallback) {
  const raw = String(value ?? "").trim();
  return raw || fallback;
}

function safeColorPickerValue(value, fallback = "#0f172a") {
  const raw = String(value || "").trim();
  if (/^#[0-9a-f]{6}$/i.test(raw)) return raw;
  if (/^#[0-9a-f]{3}$/i.test(raw)) {
    return `#${raw.slice(1).split("").map((part) => part + part).join("")}`;
  }
  return fallback;
}

function presetFontSizePx(size) {
  // 15 CSS px is approximately 11.25 pt, which is visually close to a normal
  // Microsoft Word report body/equation size without making Math look oversized.
  if (size === "small") return 13;
  if (size === "large") return 17;
  return 15;
}

function presetWidthPx(widthMode) {
  if (widthMode === "medium") return 520;
  if (widthMode === "wide") return 660;
  if (widthMode === "full") return 760;
  return 420;
}


function createMathSupplementalId(prefix = "line") {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return `${prefix}-${crypto.randomUUID()}`;
    }
  } catch {}
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
}

function stableMathSupplementalId(kind, index, text) {
  const source = `${kind}:${index}:${String(text || "")}`;
  let hash = 2166136261;
  for (let i = 0; i < source.length; i += 1) {
    hash ^= source.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return `${kind}line-${(hash >>> 0).toString(36)}`;
}

function normalizeMathSupplementalLine(line, kind = "title", index = 0) {
  const source = line && typeof line === "object" && !Array.isArray(line)
    ? line
    : { text: String(line ?? "") };
  const normalizedKind = kind === "note" ? "note" : "title";
  const text = String(source.text ?? source.value ?? "");
  return {
    id: String(source.id || stableMathSupplementalId(normalizedKind, index, text)),
    text,
    alignment: normalizeAlignment(
      source.alignment,
      "center"
    ),
    fontSizePx: clamp(
      finiteNumber(source.fontSizePx, normalizedKind === "title" ? 14 : 12),
      3,
      96
    ),
    bold:
      typeof source.bold === "boolean"
        ? source.bold
        : normalizedKind === "title",
    italic: source.italic === true,
    underline: source.underline === true,
  };
}

function normalizeMathSupplementalLines(value, kind = "title") {
  if (!Array.isArray(value)) return [];
  return value.map((line, index) =>
    normalizeMathSupplementalLine(line, kind, index)
  );
}

function isPlainTextMathLine(line) {
  const source = String(line || "").trim();
  if (!source || !/[A-Za-z]/.test(source)) return false;
  if (isLikelyLatex(source)) return false;

  // Any clear equation/operator signal keeps the line in the KaTeX path.
  if (/[=+\-*/^<>≤≥≠≈±×÷∫∂∑√]/.test(source)) return false;
  if (/\b(?:sqrt|sin|cos|tan|log|ln|lim|sum|integral|derivative)\b/i.test(source)) {
    return false;
  }

  const words = source.match(/[A-Za-z][A-Za-z'’-]*/g) || [];
  return words.length >= 2;
}

function defaultMetaFor(rawContent) {
  // Math should arrive in the document looking finished without guessing from the
  // number of lines. One line or one hundred lines use the same calm default:
  // full printable width, centered equation group, and no manual offset.
  return {
    alignment: "center",
    size: "medium",
    widthMode: "full",
    screenVariant: "soft",
    showKindLabel: false,
    showSummary: false,
    kindLabelText: "",
    summaryText: "",
    topTitles: [],
    bottomNotes: [],
    fontSizePx: 15,
    mathColor: TEXT_DARK,
    backgroundColor: "",
    borderColor: "",
    borderWidthPx: 1,
    borderRadiusPx: 10,
    paddingX: 16,
    paddingY: 14,
    lineGapPx: 8,
    lineHeight: 1.45,
    widthPx: 760,
    heightPx: 0,
    minHeightPx: 0,
    topEdgeInsetPx: 0,
    contentOffsetX: 0,
    contentOffsetY: 0,
    kindLabelColor: TEXT_MUTED,
    summaryColor: TEXT_MUTED,
    shadow: "none",
    layoutVersion: DOC_WRITER_MATH_LAYOUT_VERSION,
  };
}

function migrateLegacyAutomaticMathMeta(rawContent, incomingMeta = {}) {
  const meta = incomingMeta && typeof incomingMeta === "object" ? { ...incomingMeta } : {};
  const currentVersion = finiteNumber(meta.layoutVersion, 0);
  if (currentVersion >= DOC_WRITER_MATH_LAYOUT_VERSION) return meta;

  // v6 already represented explicit user-owned layout. Never reinterpret those
  // saved choices while moving to v9; only stamp the new schema version.
  if (currentVersion >= 6) {
    return { ...meta, layoutVersion: DOC_WRITER_MATH_LAYOUT_VERSION };
  }

  const lines = splitMathLines(String(rawContent || ""));
  const multiLine = lines.length > 1;
  const alignment = normalizeAlignment(meta.alignment, multiLine ? "left" : "center");
  const widthMode = normalizeWidthMode(meta.widthMode || (multiLine ? "wide" : "content"));
  const widthPx = finiteNumber(meta.widthPx, multiLine ? 660 : 420);
  const offsetX = finiteNumber(meta.contentOffsetX, 0);
  const offsetY = finiteNumber(meta.contentOffsetY, 0);

  // v5 automatically produced exactly these two layouts. Upgrade only those
  // legacy automatic combinations; explicit custom/medium/right/etc. choices are
  // preserved. This lets existing generated Math immediately adopt the new default.
  const legacyMultiAuto =
    multiLine && alignment === "left" && widthMode === "wide" && Math.abs(widthPx - 660) < 0.5;
  const legacySingleAuto =
    !multiLine && alignment === "center" && widthMode === "content" && Math.abs(widthPx - 420) < 0.5;

  if (legacyMultiAuto || legacySingleAuto) {
    return {
      ...meta,
      alignment: "center",
      widthMode: "full",
      widthPx: 760,
      contentOffsetX: offsetX === 0 ? 0 : offsetX,
      contentOffsetY: offsetY === 0 ? 0 : offsetY,
      layoutVersion: DOC_WRITER_MATH_LAYOUT_VERSION,
    };
  }

  return { ...meta, layoutVersion: DOC_WRITER_MATH_LAYOUT_VERSION };
}

function normalizeMathMeta(rawContent, incomingMeta = {}) {
  const defaults = defaultMetaFor(rawContent);
  const merged = { ...defaults, ...(incomingMeta || {}) };
  const size = normalizeSizePreset(merged.size);
  const widthMode = normalizeWidthMode(merged.widthMode);
  return {
    ...merged,
    alignment: normalizeAlignment(merged.alignment, defaults.alignment),
    size,
    widthMode,
    layoutVersion: DOC_WRITER_MATH_LAYOUT_VERSION,
    screenVariant: normalizeScreenVariant(merged.screenVariant),
    showKindLabel: merged.showKindLabel === true,
    showSummary: merged.showSummary === true,
    kindLabelText: String(merged.kindLabelText ?? ""),
    summaryText: String(merged.summaryText ?? ""),
    topTitles: normalizeMathSupplementalLines(merged.topTitles, "title"),
    bottomNotes: normalizeMathSupplementalLines(merged.bottomNotes, "note"),
    fontSizePx: clamp(finiteNumber(merged.fontSizePx, presetFontSizePx(size)), 6, 72),
    mathColor: normalizeColorValue(merged.mathColor, TEXT_DARK),
    backgroundColor: String(merged.backgroundColor ?? ""),
    borderColor: String(merged.borderColor ?? ""),
    borderWidthPx: clamp(finiteNumber(merged.borderWidthPx, 1), 0, 12),
    borderRadiusPx: clamp(finiteNumber(merged.borderRadiusPx, 10), 0, 40),
    paddingX: clamp(finiteNumber(merged.paddingX, SIZE_CONFIG[size]?.paddingX || 20), 0, 80),
    paddingY: clamp(finiteNumber(merged.paddingY, SIZE_CONFIG[size]?.paddingY || 18), 0, 80),
    lineGapPx: clamp(finiteNumber(merged.lineGapPx, SIZE_CONFIG[size]?.lineGap || 10), 0, 60),
    lineHeight: clamp(finiteNumber(merged.lineHeight, 1.55), 0.9, 3),
    widthPx: clamp(finiteNumber(merged.widthPx, presetWidthPx(widthMode)), DOC_WRITER_MATH_MIN_WIDTH_PX, DOC_WRITER_MATH_MAX_WIDTH_PX),
    heightPx: clamp(finiteNumber(merged.heightPx, 0), 0, DOC_WRITER_MATH_MAX_HEIGHT_PX),
    minHeightPx: clamp(finiteNumber(merged.minHeightPx, 0), 0, DOC_WRITER_MATH_MAX_HEIGHT_PX),
    topEdgeInsetPx: clamp(finiteNumber(merged.topEdgeInsetPx, 0), 0, DOC_WRITER_MATH_MAX_HEIGHT_PX),
    contentOffsetX: clamp(finiteNumber(merged.contentOffsetX, 0), -800, 800),
    contentOffsetY: clamp(finiteNumber(merged.contentOffsetY, 0), -800, 800),
    kindLabelColor: normalizeColorValue(merged.kindLabelColor, TEXT_MUTED),
    summaryColor: normalizeColorValue(merged.summaryColor, TEXT_MUTED),
    shadow: ["none", "soft", "strong"].includes(String(merged.shadow || "").toLowerCase())
      ? String(merged.shadow).toLowerCase()
      : "none",
  };
}

function normalizeIncomingValue(value) {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    const raw = String(value.raw ?? value.source ?? value.value ?? value.latex ?? "");
    return {
      raw,
      meta: normalizeMathMeta(
        raw,
        migrateLegacyAutomaticMathMeta(
          raw,
          value.meta && typeof value.meta === "object" ? value.meta : {}
        )
      ),
    };
  }

  const text = String(value ?? "").trim();
  const parsed = text.startsWith("{") ? safeJsonParse(text) : null;
  if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
    const raw = String(parsed.raw ?? parsed.source ?? parsed.value ?? parsed.latex ?? "");
    return {
      raw,
      meta: normalizeMathMeta(
        raw,
        migrateLegacyAutomaticMathMeta(
          raw,
          parsed.meta && typeof parsed.meta === "object" ? parsed.meta : {}
        )
      ),
    };
  }

  return {
    raw: text,
    meta: normalizeMathMeta(text, {}),
  };
}

function serializeMathValue(raw, meta) {
  const normalizedMeta = normalizeMathMeta(raw, meta);
  return {
    type: "docwriter_math",
    raw: String(raw || ""),
    meta: {
      alignment: normalizedMeta.alignment,
      size: normalizedMeta.size,
      widthMode: normalizedMeta.widthMode,
      layoutVersion: DOC_WRITER_MATH_LAYOUT_VERSION,
      screenVariant: normalizedMeta.screenVariant,
      showKindLabel: normalizedMeta.showKindLabel === true,
      showSummary: normalizedMeta.showSummary === true,
      kindLabelText: normalizedMeta.kindLabelText,
      summaryText: normalizedMeta.summaryText,
      topTitles: normalizedMeta.topTitles.map((line) => ({ ...line })),
      bottomNotes: normalizedMeta.bottomNotes.map((line) => ({ ...line })),
      fontSizePx: normalizedMeta.fontSizePx,
      mathColor: normalizedMeta.mathColor,
      backgroundColor: normalizedMeta.backgroundColor,
      borderColor: normalizedMeta.borderColor,
      borderWidthPx: normalizedMeta.borderWidthPx,
      borderRadiusPx: normalizedMeta.borderRadiusPx,
      paddingX: normalizedMeta.paddingX,
      paddingY: normalizedMeta.paddingY,
      lineGapPx: normalizedMeta.lineGapPx,
      lineHeight: normalizedMeta.lineHeight,
      widthPx: normalizedMeta.widthPx,
      heightPx: normalizedMeta.heightPx,
      minHeightPx: normalizedMeta.minHeightPx,
      topEdgeInsetPx: normalizedMeta.topEdgeInsetPx,
      contentOffsetX: normalizedMeta.contentOffsetX,
      contentOffsetY: normalizedMeta.contentOffsetY,
      kindLabelColor: normalizedMeta.kindLabelColor,
      summaryColor: normalizedMeta.summaryColor,
      shadow: normalizedMeta.shadow,
    },
  };
}

function resolvedWidthStyle(meta) {
  const normalized = normalizeMathMeta("", meta);
  if (normalized.widthMode === "custom") {
    return `${normalized.widthPx}px`;
  }
  return WIDTH_MODES[normalized.widthMode]?.width || "fit-content";
}

function resolvedShadow(meta) {
  const value = String(meta?.shadow || "none").toLowerCase();
  if (value === "strong") return "0 18px 40px rgba(15,23,42,0.16)";
  if (value === "soft") return "0 10px 24px rgba(15,23,42,0.10)";
  return "none";
}

function resolvedSurfaceColors(meta) {
  const normalized = normalizeMathMeta("", meta);
  const variant = SCREEN_VARIANTS[normalized.screenVariant] || SCREEN_VARIANTS.soft;
  const background = normalized.backgroundColor || variant.background;
  const borderColor = normalized.borderColor || variant.border;
  const borderWidth = normalized.screenVariant === "plain" && !normalized.borderColor
    ? 0
    : normalized.borderWidthPx;
  return { background, borderColor, borderWidth };
}

function DraftNumberInput({
  value,
  fallback,
  min,
  max,
  step = 1,
  onCommit,
  style,
  ariaLabel,
}) {
  const normalizedInitial = String(value ?? fallback ?? "");
  const [text, setText] = useState(normalizedInitial);

  useEffect(() => {
    setText(String(value ?? fallback ?? ""));
  }, [value, fallback]);

  const commit = useCallback((rawValue = text) => {
    const parsed = Number.parseFloat(String(rawValue));
    if (!Number.isFinite(parsed)) {
      setText(String(value ?? fallback ?? ""));
      return;
    }
    const next = clamp(parsed, min, max);
    setText(String(Number.isInteger(next) ? next : Number(next.toFixed(3))));
    onCommit?.(next);
  }, [fallback, max, min, onCommit, text, value]);

  return (
    <input
      type="number"
      aria-label={ariaLabel}
      min={min}
      max={max}
      step={step}
      value={text}
      onChange={(event) => {
        const nextText = event.target.value;
        setText(nextText);
        if (nextText === "" || nextText === "-" || nextText === ".") return;
        const parsed = Number.parseFloat(nextText);
        if (Number.isFinite(parsed) && parsed >= min && parsed <= max) {
          onCommit?.(parsed);
        }
      }}
      onBlur={() => commit()}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          commit(event.currentTarget.value);
          event.currentTarget.blur();
        }
      }}
      style={style}
    />
  );
}

function ColorControl({ label, value, fallback, onChange, inputStyle, labelStyle }) {
  const resolved = String(value || fallback || "");
  return (
    <label style={labelStyle}>
      {label}
      <div style={{ display: "grid", gridTemplateColumns: "42px minmax(0,1fr)", gap: 7 }}>
        <input
          type="color"
          value={safeColorPickerValue(resolved, safeColorPickerValue(fallback, "#0f172a"))}
          onChange={(event) => onChange?.(event.target.value)}
          style={{ width: 42, height: 34, padding: 2, border: "1px solid #cbd5e1", borderRadius: 8, background: "#fff", cursor: "pointer" }}
        />
        <input
          value={resolved}
          onChange={(event) => onChange?.(event.target.value)}
          placeholder={fallback}
          style={inputStyle}
        />
      </div>
    </label>
  );
}

function MathSurface({
  rawValue,
  metaValue,
  interactive = false,
  onMetaPatch,
  surfaceRef = null,
  showPlaceholder = true,
  editorChrome = null,
  onActivate = null,
  supplementalEditable = false,
  onSupplementalFocus = null,
  onSupplementalCommit = null,
  canvasTextAlignmentHandoff = false,
}) {
  const normalizedMeta = useMemo(() => normalizeMathMeta(rawValue, metaValue), [rawValue, metaValue]);
  const normalizedContent = useMemo(() => normalizeContent(rawValue), [rawValue]);
  const rawLines = useMemo(() => splitMathLines(normalizedContent), [normalizedContent]);
  const displayLines = useMemo(
    () =>
      rawLines.map((line) => ({
        original: line,
        plainText: isPlainTextMathLine(line),
        latex: normalizePlainToLatex(line),
      })),
    [rawLines]
  );
  const kind = useMemo(() => detectMathKind(normalizedContent), [normalizedContent]);
  const summary = useMemo(() => buildReadableSummary(kind, normalizedContent), [kind, normalizedContent]);
  const labelText = String(normalizedMeta.kindLabelText || "").trim() || kind;
  const descriptionText = String(normalizedMeta.summaryText || "").trim() || summary;
  const dragRef = useRef(null);
  const colors = resolvedSurfaceColors(normalizedMeta);
  const alignment = normalizeAlignment(normalizedMeta.alignment, "center");

  // Canvas handoff only: seed the exact Math-preview alignment as an inline
  // important declaration so DocCanvasEditor's ordinary left-aligned text rule
  // cannot visually override it. The marker prevents React rerenders from
  // repeatedly reasserting the same Math alignment after a user later changes
  // the line with the DocCanvas toolbar. If Math itself changes the alignment,
  // the new value is handed off once again.
  const handoffCanvasTextAlignment = (node, requestedAlignment) => {
    if (!node || !canvasTextAlignmentHandoff) return;
    const nextAlignment = normalizeAlignment(requestedAlignment, "center");
    const marker = "data-docwriter-math-canvas-alignment-handoff";
    if (node.getAttribute(marker) === nextAlignment) return;
    node.style.setProperty("text-align", nextAlignment, "important");
    node.setAttribute(marker, nextAlignment);
  };

  const beginContentDrag = (event) => {
    if (!interactive || event.button !== 0 || !onMetaPatch) return;
    event.preventDefault();
    event.stopPropagation();
    dragRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      startOffsetX: normalizedMeta.contentOffsetX,
      startOffsetY: normalizedMeta.contentOffsetY,
      moved: false,
    };
    try { event.currentTarget.setPointerCapture?.(event.pointerId); } catch {}

    const move = (moveEvent) => {
      const state = dragRef.current;
      if (!state) return;
      const deltaX = moveEvent.clientX - state.startX;
      const deltaY = moveEvent.clientY - state.startY;
      if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) state.moved = true;
      onMetaPatch({
        contentOffsetX: Math.round(state.startOffsetX + deltaX),
        contentOffsetY: Math.round(state.startOffsetY + deltaY),
      });
    };
    const finish = () => {
      const state = dragRef.current;
      window.removeEventListener("pointermove", move, true);
      window.removeEventListener("pointerup", finish, true);
      window.removeEventListener("pointercancel", finish, true);
      dragRef.current = null;
      if (state && !state.moved) {
        try { onActivate?.(); } catch {}
      }
    };
    window.addEventListener("pointermove", move, true);
    window.addEventListener("pointerup", finish, true);
    window.addEventListener("pointercancel", finish, true);
  };


  const supplementalLine = (line, supplementalKind) => {
    const isTitle = supplementalKind === "title";
    const lineStyle = {
      minHeight: 18,
      width: "100%",
      boxSizing: "border-box",
      textAlign: line.alignment,
      fontSize: `${line.fontSizePx}px`,
      fontWeight: line.bold ? 700 : 400,
      fontStyle: line.italic ? "italic" : "normal",
      textDecoration: line.underline ? "underline" : "none",
      lineHeight: 1.35,
      whiteSpace: "pre-wrap",
      overflowWrap: "anywhere",
      color: normalizedMeta.mathColor,
      margin: isTitle ? "0 0 4px" : "4px 0 0",
      padding: "1px 0",
      outline: "none",
      cursor: supplementalEditable ? "text" : "default",
      userSelect: supplementalEditable ? "text" : "auto",
    };

    return (
      <div
        key={`${supplementalKind}-${line.id}`}
        ref={(node) => handoffCanvasTextAlignment(node, line.alignment)}
        data-docwriter-math-supplemental-kind={supplementalKind}
        data-docwriter-math-supplemental-id={line.id}
        contentEditable={supplementalEditable || undefined}
        suppressContentEditableWarning
        spellCheck={supplementalEditable}
        onFocus={() => {
          if (supplementalEditable) {
            try { onSupplementalFocus?.(supplementalKind, line.id); } catch {}
          }
        }}
        onPointerDown={(event) => {
          if (!supplementalEditable) return;
          event.stopPropagation();
        }}
        onClick={(event) => {
          if (!supplementalEditable) return;
          event.stopPropagation();
        }}
        onKeyDown={(event) => {
          if (!supplementalEditable) return;
          event.stopPropagation();
          // Each Add action creates a new independent title/note line. Keep a
          // single line single-line so Enter does not accidentally nest blocks.
          if (event.key === "Enter") event.preventDefault();
        }}
        onBlur={(event) => {
          if (!supplementalEditable) return;
          const nextText = String(event.currentTarget.textContent || "");
          try {
            onSupplementalCommit?.(supplementalKind, line.id, nextText);
          } catch {}
        }}
        style={lineStyle}
      >
        {line.text}
      </div>
    );
  };

  const surfaceStyle = {
    position: "relative",
    width: "100%",
    minWidth: 0,
    height: normalizedMeta.heightPx > 0 ? `${normalizedMeta.heightPx}px` : undefined,
    minHeight:
      normalizedMeta.heightPx > 0
        ? 0
        : normalizedMeta.minHeightPx > 0
          ? `${normalizedMeta.minHeightPx}px`
          : undefined,
    marginTop: normalizedMeta.topEdgeInsetPx > 0
      ? `${normalizedMeta.topEdgeInsetPx}px`
      : 0,
    boxSizing: "border-box",
    padding: `${normalizedMeta.paddingY}px ${normalizedMeta.paddingX}px`,
    borderRadius: `${normalizedMeta.borderRadiusPx}px`,
    background: colors.background,
    border: colors.borderWidth > 0 ? `${colors.borderWidth}px solid ${colors.borderColor}` : "none",
    boxShadow: resolvedShadow(normalizedMeta),
    overflow: "hidden",
    textAlign: alignment,
    breakInside: "avoid",
    pageBreakInside: "avoid",
    color: normalizedMeta.mathColor,
  };

  const contentStyle = {
    position: "relative",
    fontSize: `${normalizedMeta.fontSizePx}px`,
    lineHeight: normalizedMeta.lineHeight,
    display: "flex",
    flexDirection: "column",
    width: "100%",
    minWidth: 0,
    gap: `${normalizedMeta.lineGapPx}px`,
    color: normalizedMeta.mathColor,
    textAlign: alignment,
    transform: `translate(${normalizedMeta.contentOffsetX}px, ${normalizedMeta.contentOffsetY}px)`,
    transformOrigin:
      alignment === "left"
        ? "left center"
        : alignment === "right"
          ? "right center"
          : "center center",
    cursor: interactive ? "grab" : "default",
    touchAction: interactive ? "none" : "auto",
    userSelect: interactive ? "none" : "text",
  };

  return (
    <div
      ref={surfaceRef}
      className="docwriter-math-print-surface"
      data-docwriter-math-print-surface="true"
      data-docwriter-math-alignment={alignment}
      data-docwriter-math-size={normalizedMeta.size}
      data-docwriter-math-width-mode={normalizedMeta.widthMode}
      style={surfaceStyle}
    >
      {editorChrome}
      {normalizedMeta.topTitles.map((line) => supplementalLine(line, "title"))}
      {normalizedMeta.showKindLabel ? (
        <div
          ref={(node) => handoffCanvasTextAlignment(node, alignment)}
          style={{ margin: "0 0 6px", color: normalizedMeta.kindLabelColor, fontSize: 11, fontWeight: 800, letterSpacing: ".06em", textTransform: "uppercase", textAlign: alignment }}
        >
          {labelText}
        </div>
      ) : null}
      {normalizedMeta.showSummary && descriptionText ? (
        <div
          ref={(node) => handoffCanvasTextAlignment(node, alignment)}
          style={{ margin: "0 0 10px", color: normalizedMeta.summaryColor, fontSize: 12, lineHeight: 1.45, textAlign: alignment }}
        >
          {descriptionText}
        </div>
      ) : null}

      <div
        className="docwriter-math-content"
        aria-label={`${kind} content`}
        title={interactive ? "Drag the rendered equation to reposition it" : undefined}
        onPointerDown={beginContentDrag}
        style={contentStyle}
      >
        {displayLines.length ? (
          displayLines.map((line, index) =>
            line.plainText ? (
              <div
                className="docwriter-math-line docwriter-math-line-text"
                key={`docwriter-plain-text-${index}`}
                style={{
                  width: "100%",
                  minWidth: 0,
                  boxSizing: "border-box",
                  textAlign: alignment,
                  whiteSpace: "pre-wrap",
                  overflowWrap: "anywhere",
                  fontFamily: 'Arial, Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                  fontStyle: "normal",
                }}
              >
                {line.original}
              </div>
            ) : (
              <SafeBlockMath
                key={`docwriter-safe-math-${index}`}
                latex={line.latex}
                original={line.original}
                index={index}
                alignment={alignment}
              />
            )
          )
        ) : showPlaceholder ? (
          <div style={{ color: TEXT_MUTED, textAlign: alignment, padding: "16px 8px" }}>Enter a formula or equation.</div>
        ) : null}
      </div>
      {normalizedMeta.bottomNotes.map((line) => supplementalLine(line, "note"))}
    </div>
  );
}


function printableMathSnapshotHtml(surface) {
  if (!surface?.cloneNode) return "";

  try {
    const clone = surface.cloneNode(true);

    // KaTeX emits two parallel representations:
    // 1) .katex-html — the visible typeset equation.
    // 2) .katex-mathml — an accessibility representation normally hidden by CSS.
    //
    // The Doc Writer snapshot is later rendered outside the live KaTeX stylesheet
    // context, so preserving both can make the same equation print twice. Keep the
    // visible HTML representation only; the canonical source remains separately
    // serialized in the DocWriterMath value.
    clone.querySelectorAll?.(".katex-mathml").forEach((node) => node.remove());

    clone
      .querySelectorAll?.(`[${DOC_WRITER_MATH_EDITOR_ONLY_ATTR}="true"]`)
      .forEach((node) => node.remove());

    clone.querySelectorAll?.("[contenteditable]").forEach((node) => {
      node.removeAttribute("contenteditable");
      node.removeAttribute("spellcheck");
    });

    return clone.outerHTML || "";
  } catch {
    return String(surface.outerHTML || "");
  }
}

export default function DocWriterMath({
  value,
  readOnly = false,
  blockId = "",
  onChange,
  onDelete,
  onRequestDrag,
  onSnapshotReady,
  onMetaChange,
  onEditingChange,
  className = "",
}) {
  const normalizedIncoming = useMemo(() => normalizeIncomingValue(value), [value]);
  const [raw, setRaw] = useState(normalizedIncoming.raw);
  const [meta, setMeta] = useState(normalizedIncoming.meta);
  const [isEditing, setIsEditing] = useState(false);
  const [draftRaw, setDraftRaw] = useState(normalizedIncoming.raw);
  const [draftMeta, setDraftMeta] = useState(normalizedIncoming.meta);
  const [draftUndoStack, setDraftUndoStack] = useState([]);
  const [draftRedoStack, setDraftRedoStack] = useState([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [activeDraftSupplemental, setActiveDraftSupplemental] = useState({ kind: "", id: "" });
  const [resizeTooltip, setResizeTooltip] = useState(null);
  const printableRef = useRef(null);
  const rootRef = useRef(null);
  const resizeRef = useRef(null);
  const moveRef = useRef(null);
  const pendingLocalCommitRef = useRef(null);
  const draftSourceRef = useRef(null);
  const draftPreviewSurfaceRef = useRef(null);
  const draftResizeRef = useRef(null);
  const draftStateRef = useRef({
    raw: normalizedIncoming.raw,
    meta: { ...normalizedIncoming.meta },
  });
  const DRAFT_HISTORY_LIMIT = 120;

  const normalizedContent = useMemo(() => normalizeContent(raw), [raw]);
  const kind = useMemo(() => detectMathKind(normalizedContent), [normalizedContent]);
  const normalizedMeta = useMemo(() => normalizeMathMeta(raw, meta), [raw, meta]);
  const serializedValue = useMemo(() => serializeMathValue(raw, normalizedMeta), [raw, normalizedMeta]);
  const ownerDocument = rootRef.current?.ownerDocument || printableRef.current?.ownerDocument ||
    (typeof document !== "undefined" ? document : null);

  useEffect(() => {
    if (isEditing || resizeRef.current || moveRef.current) return;

    // A parent/editor render can briefly hand this component the pre-save value
    // immediately after an explicit user edit. Never let that stale echo undo a
    // just-saved alignment/size/appearance choice. Once the parent reflects the
    // committed value, or the actual raw equation changes, normal syncing resumes.
    const pending = pendingLocalCommitRef.current;
    if (pending) {
      const incomingSignature = JSON.stringify(
        serializeMathValue(normalizedIncoming.raw, normalizedIncoming.meta)
      );

      if (incomingSignature === pending.signature) {
        pendingLocalCommitRef.current = null;
      } else if (normalizedIncoming.raw === pending.raw) {
        return;
      } else {
        pendingLocalCommitRef.current = null;
      }
    }

    setRaw(normalizedIncoming.raw);
    setMeta(normalizedIncoming.meta);
  }, [normalizedIncoming.raw, normalizedIncoming.meta, isEditing]);

  const notifyEditing = useCallback((open, detail = {}) => {
    try { onEditingChange?.(Boolean(open), { blockId, kind, ...detail }); } catch {}
  }, [blockId, kind, onEditingChange]);

  const commitValue = useCallback((nextRawValue, nextMetaValue, detail = {}) => {
    const nextRaw = String(nextRawValue ?? "");
    const nextMeta = normalizeMathMeta(nextRaw, nextMetaValue);
    const nextValue = serializeMathValue(nextRaw, nextMeta);
    pendingLocalCommitRef.current = {
      raw: nextRaw,
      signature: JSON.stringify(nextValue),
    };
    setRaw(nextRaw);
    setMeta(nextMeta);
    try {
      onChange?.(nextValue, {
        raw: nextRaw,
        meta: nextMeta,
        kind: detectMathKind(nextRaw),
        ...detail,
      });
    } catch {}
    try { onMetaChange?.(nextMeta, nextValue); } catch {}
    return nextValue;
  }, [onChange, onMetaChange]);

  const snapshotDraftState = useCallback(() => {
    const current = draftStateRef.current || {};
    return {
      raw: String(current.raw ?? ""),
      meta: { ...(current.meta || {}) },
    };
  }, []);

  const draftSnapshotSignature = useCallback((snapshot) => {
    if (!snapshot) return "";
    try {
      return JSON.stringify(serializeMathValue(snapshot.raw, snapshot.meta));
    } catch {
      return JSON.stringify(snapshot);
    }
  }, []);

  const restoreDraftSnapshot = useCallback((snapshot) => {
    if (!snapshot) return;
    const next = {
      raw: String(snapshot.raw ?? ""),
      meta: { ...(snapshot.meta || {}) },
    };
    draftStateRef.current = next;
    setDraftRaw(next.raw);
    setDraftMeta(next.meta);
  }, []);

  const captureDraftCheckpoint = useCallback(() => {
    const snapshot = snapshotDraftState();
    const signature = draftSnapshotSignature(snapshot);
    setDraftUndoStack((current) => {
      const last = current[current.length - 1];
      if (last && draftSnapshotSignature(last) === signature) return current;
      return [...current, snapshot].slice(-DRAFT_HISTORY_LIMIT);
    });
    setDraftRedoStack([]);
  }, [draftSnapshotSignature, snapshotDraftState]);

  const undoDraftChange = useCallback(() => {
    if (!draftUndoStack.length) return;
    const previous = draftUndoStack[draftUndoStack.length - 1];
    const current = snapshotDraftState();
    setDraftUndoStack(draftUndoStack.slice(0, -1));
    setDraftRedoStack((stack) => [...stack, current].slice(-DRAFT_HISTORY_LIMIT));
    restoreDraftSnapshot(previous);
  }, [draftUndoStack, restoreDraftSnapshot, snapshotDraftState]);

  const redoDraftChange = useCallback(() => {
    if (!draftRedoStack.length) return;
    const next = draftRedoStack[draftRedoStack.length - 1];
    const current = snapshotDraftState();
    setDraftRedoStack(draftRedoStack.slice(0, -1));
    setDraftUndoStack((stack) => [...stack, current].slice(-DRAFT_HISTORY_LIMIT));
    restoreDraftSnapshot(next);
  }, [draftRedoStack, restoreDraftSnapshot, snapshotDraftState]);

  const updateDraftRaw = useCallback((nextRawValue, { history = true } = {}) => {
    const nextRaw = String(nextRawValue ?? "");
    const current = draftStateRef.current || { raw: "", meta: {} };
    if (String(current.raw ?? "") === nextRaw) return;
    if (history) captureDraftCheckpoint();
    draftStateRef.current = { ...current, raw: nextRaw };
    setDraftRaw(nextRaw);
  }, [captureDraftCheckpoint]);

  const patchDraftMeta = useCallback((patch, { history = true } = {}) => {
    const current = draftStateRef.current || { raw: "", meta: {} };
    const nextMeta = { ...(current.meta || {}), ...(patch || {}) };
    let changed = true;
    try { changed = JSON.stringify(nextMeta) !== JSON.stringify(current.meta || {}); } catch {}
    if (!changed) return;
    if (history) captureDraftCheckpoint();
    draftStateRef.current = { ...current, meta: nextMeta };
    setDraftMeta(nextMeta);
  }, [captureDraftCheckpoint]);

  const openEditor = useCallback(() => {
    const nextDraft = { raw, meta: { ...normalizedMeta } };
    draftStateRef.current = nextDraft;
    setDraftRaw(nextDraft.raw);
    setDraftMeta(nextDraft.meta);
    setDraftUndoStack([]);
    setDraftRedoStack([]);
    setDeleteConfirmOpen(false);
    setActiveDraftSupplemental({ kind: "", id: "" });
    // Signal the Editor before focus leaves the contentEditable canvas.
    notifyEditing(true, { operation: "edit" });
    setIsEditing(true);
  }, [normalizedMeta, notifyEditing, raw]);

  const closeEditor = useCallback(() => {
    const nextDraft = { raw, meta: { ...normalizedMeta } };
    draftStateRef.current = nextDraft;
    setDraftRaw(nextDraft.raw);
    setDraftMeta(nextDraft.meta);
    setDraftUndoStack([]);
    setDraftRedoStack([]);
    setDeleteConfirmOpen(false);
    setActiveDraftSupplemental({ kind: "", id: "" });
    setIsEditing(false);
    notifyEditing(false, { operation: "edit" });
  }, [normalizedMeta, notifyEditing, raw]);

  const saveEditor = useCallback(() => {
    const current = snapshotDraftState();
    const nextMeta = normalizeMathMeta(current.raw, current.meta);
    commitValue(current.raw, nextMeta, { operation: "edit-save" });
    draftStateRef.current = { raw: current.raw, meta: { ...nextMeta } };
    setDraftRaw(current.raw);
    setDraftMeta(nextMeta);
    setDraftUndoStack([]);
    setDraftRedoStack([]);
    setDeleteConfirmOpen(false);
    setActiveDraftSupplemental({ kind: "", id: "" });
    setIsEditing(false);
    notifyEditing(false, { operation: "edit-save" });
  }, [commitValue, notifyEditing, snapshotDraftState]);

  const requestDeleteEquation = useCallback(() => {
    setDeleteConfirmOpen(true);
  }, []);

  const deleteEquation = useCallback(() => {
    setDeleteConfirmOpen(false);
    setDraftUndoStack([]);
    setDraftRedoStack([]);
    setIsEditing(false);
    notifyEditing(false, { operation: "delete" });
    try { onDelete?.(serializedValue); } catch {}
  }, [notifyEditing, onDelete, serializedValue]);


  const commitLiveSupplementalText = useCallback((supplementalKind, lineId, nextText) => {
    if (readOnly) return;
    const key = supplementalKind === "note" ? "bottomNotes" : "topTitles";
    const currentMeta = normalizeMathMeta(raw, meta);
    const currentLines = Array.isArray(currentMeta[key]) ? currentMeta[key] : [];
    let changed = false;
    const nextLines = currentLines.map((line) => {
      if (line.id !== lineId) return line;
      const textValue = String(nextText ?? "");
      if (line.text === textValue) return line;
      changed = true;
      return { ...line, text: textValue };
    });
    if (!changed) return;
    commitValue(
      raw,
      { ...currentMeta, [key]: nextLines },
      {
        operation: "supplemental-edit",
        supplementalKind,
        supplementalId: lineId,
      }
    );
  }, [commitValue, meta, raw, readOnly]);

  const emitSnapshot = useCallback(() => {
    if (!printableRef.current || typeof onSnapshotReady !== "function") return "";
    const html = printableMathSnapshotHtml(printableRef.current);
    try {
      onSnapshotReady({
        blockId,
        kind,
        html,
        text: normalizedContent,
        value: serializedValue,
      });
    } catch {}
    return html;
  }, [blockId, kind, normalizedContent, onSnapshotReady, serializedValue]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    let frame1 = window.requestAnimationFrame(() => {
      frame1 = window.requestAnimationFrame(() => emitSnapshot());
    });
    return () => {
      if (frame1) window.cancelAnimationFrame(frame1);
    };
  }, [emitSnapshot]);

  useEffect(() => () => {
    if (isEditing || resizeRef.current || moveRef.current) {
      notifyEditing(false, { operation: "unmount" });
    }
  }, [isEditing, notifyEditing]);

  const beginResize = (event, handle = "se") => {
    if (readOnly || !event || event.button !== 0) return;

    event.preventDefault();
    event.stopPropagation();
    setResizeTooltip(null);

    const rootRect = rootRef.current?.getBoundingClientRect?.();
    const surfaceRect = printableRef.current?.getBoundingClientRect?.();
    const startWidth = clamp(
      Math.round(rootRect?.width || surfaceRect?.width || normalizedMeta.widthPx || 420),
      DOC_WRITER_MATH_MIN_WIDTH_PX,
      DOC_WRITER_MATH_MAX_WIDTH_PX
    );
    const startHeight = clamp(
      Math.round(
        normalizedMeta.heightPx > 0
          ? normalizedMeta.heightPx
          : surfaceRect?.height || normalizedMeta.minHeightPx || DOC_WRITER_MATH_MIN_HEIGHT_PX
      ),
      DOC_WRITER_MATH_MIN_HEIGHT_PX,
      DOC_WRITER_MATH_MAX_HEIGHT_PX
    );

    const resizeHorizontal = handle.includes("e") || handle.includes("w");
    const resizeFromWest = handle.includes("w");
    const resizeFromNorth = handle.includes("n");
    const resizeFromTopEdge = handle === "n";
    const startTopEdgeInset = clamp(
      finiteNumber(normalizedMeta.topEdgeInsetPx, 0),
      0,
      DOC_WRITER_MATH_MAX_HEIGHT_PX
    );
    const state = {
      handle,
      startX: event.clientX,
      startY: event.clientY,
      startWidth,
      startHeight,
      startTopEdgeInset,
      latest: {
        ...normalizedMeta,
        widthMode: resizeHorizontal ? "custom" : normalizedMeta.widthMode,
        widthPx: resizeHorizontal ? startWidth : normalizedMeta.widthPx,
        heightPx: startHeight,
        minHeightPx: 0,
        topEdgeInsetPx: startTopEdgeInset,
      },
    };

    resizeRef.current = state;
    notifyEditing(true, {
      operation: "resize",
      handle,
      axis: resizeHorizontal ? "both" : "vertical",
    });

    try {
      event.currentTarget.setPointerCapture?.(event.pointerId);
    } catch {}

    const move = (moveEvent) => {
      if (!resizeRef.current) return;

      const deltaX = moveEvent.clientX - state.startX;
      const deltaY = moveEvent.clientY - state.startY;

      const nextWidth = resizeHorizontal
        ? clamp(
            state.startWidth + (resizeFromWest ? -deltaX : deltaX),
            DOC_WRITER_MATH_MIN_WIDTH_PX,
            DOC_WRITER_MATH_MAX_WIDTH_PX
          )
        : state.startWidth;

      // Top/bottom edge handles are vertical-only. The top edge has true
      // opposite-edge anchoring: dragging it downward reduces height and increases
      // the surface's top inset by the same amount, so the bottom edge stays fixed.
      // Left/right geometry and alignment are never rewritten by either edge.
      const nextHeight = clamp(
        state.startHeight + (resizeFromNorth ? -deltaY : deltaY),
        DOC_WRITER_MATH_MIN_HEIGHT_PX,
        DOC_WRITER_MATH_MAX_HEIGHT_PX
      );
      const actualHeightDelta = state.startHeight - nextHeight;
      const nextTopEdgeInset = resizeFromTopEdge
        ? clamp(
            state.startTopEdgeInset + actualHeightDelta,
            0,
            DOC_WRITER_MATH_MAX_HEIGHT_PX
          )
        : state.startTopEdgeInset;

      const nextMeta = {
        ...normalizedMeta,
        widthMode: resizeHorizontal ? "custom" : normalizedMeta.widthMode,
        widthPx: resizeHorizontal ? Math.round(nextWidth) : normalizedMeta.widthPx,
        heightPx: Math.round(nextHeight),
        minHeightPx: 0,
        topEdgeInsetPx: Math.round(nextTopEdgeInset),
      };

      resizeRef.current.latest = nextMeta;
      setMeta(nextMeta);
    };

    const finish = () => {
      window.removeEventListener("pointermove", move, true);
      window.removeEventListener("pointerup", finish, true);
      window.removeEventListener("pointercancel", finish, true);

      const finalState = resizeRef.current;
      resizeRef.current = null;

      if (finalState?.latest) {
        commitValue(raw, finalState.latest, {
          operation: "resize",
          handle: finalState.handle,
          axis:
            finalState.handle.includes("e") || finalState.handle.includes("w")
              ? "both"
              : "vertical",
        });
      }

      notifyEditing(false, {
        operation: "resize",
        handle,
        axis: resizeHorizontal ? "both" : "vertical",
      });
    };

    window.addEventListener("pointermove", move, true);
    window.addEventListener("pointerup", finish, true);
    window.addEventListener("pointercancel", finish, true);
  };

  if (!normalizedContent && readOnly) return null;

  const alignmentMargins = normalizedMeta.alignment === "left"
    ? { marginLeft: 0, marginRight: "auto" }
    : normalizedMeta.alignment === "right"
      ? { marginLeft: "auto", marginRight: 0 }
      : { marginLeft: "auto", marginRight: "auto" };

  const rootWidth = resolvedWidthStyle(normalizedMeta);
  const shellStyle = {
    position: "relative",
    boxSizing: "border-box",
    width: rootWidth,
    maxWidth: "100%",
    minWidth: 0,
    // 34px controls begin at y=8 and end at y=42. Start the Math surface at
    // y=48 so the controls stay visually connected without touching the box.
    paddingTop: readOnly ? 0 : 48,
    marginTop: 14,
    marginBottom: 18,
    ...alignmentMargins,
    color: TEXT_DARK,
    fontFamily: 'Arial, Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    breakInside: "avoid",
    pageBreakInside: "avoid",
    pointerEvents: "none",
  };

  const buttonStyle = {
    minHeight: 30,
    padding: "7px 12px",
    border: "1px solid rgba(15,23,42,.15)",
    borderRadius: 8,
    background: "rgba(255,255,255,.97)",
    color: TEXT_DARK,
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
    boxShadow: "0 3px 10px rgba(15,23,42,.10)",
  };

  const inputStyle = {
    width: "100%",
    boxSizing: "border-box",
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    padding: "8px 10px",
    fontSize: 13,
    color: TEXT_DARK,
    background: "#ffffff",
    outline: "none",
  };
  const labelStyle = {
    display: "grid",
    gap: 5,
    fontSize: 12,
    fontWeight: 750,
    color: TEXT_MUTED,
  };
  const sectionStyle = {
    border: "1px solid rgba(148,163,184,0.28)",
    borderRadius: 12,
    padding: 12,
    background: "#ffffff",
  };
  const sectionTitleStyle = {
    fontSize: 12,
    fontWeight: 900,
    color: TEXT_DARK,
    marginBottom: 9,
  };
  const rowGrid = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: 9,
  };

  const draftNormalizedMeta = normalizeMathMeta(draftRaw, draftMeta);
  const draftWidthStyle = resolvedWidthStyle(draftNormalizedMeta);

  const activeDraftSupplementalKey =
    activeDraftSupplemental.kind === "note" ? "bottomNotes" : "topTitles";
  const activeDraftSupplementalLine = (
    draftNormalizedMeta[activeDraftSupplementalKey] || []
  ).find((line) => line.id === activeDraftSupplemental.id) || null;

  const updateDraftSupplementalLine = (supplementalKind, lineId, patch) => {
    const key = supplementalKind === "note" ? "bottomNotes" : "topTitles";
    const lines = Array.isArray(draftNormalizedMeta[key])
      ? draftNormalizedMeta[key]
      : [];
    let changed = false;
    const nextLines = lines.map((line) => {
      if (line.id !== lineId) return line;
      const next = { ...line, ...(patch || {}) };
      try {
        changed = changed || JSON.stringify(next) !== JSON.stringify(line);
      } catch {
        changed = true;
      }
      return next;
    });
    if (changed) patchDraftMeta({ [key]: nextLines });
  };

  const addDraftSupplementalLine = (supplementalKind) => {
    const key = supplementalKind === "note" ? "bottomNotes" : "topTitles";
    const nextLine = normalizeMathSupplementalLine(
      {
        id: createMathSupplementalId(
          supplementalKind === "note" ? "noteline" : "titleline"
        ),
        text: "",
      },
      supplementalKind,
      (draftNormalizedMeta[key] || []).length
    );
    patchDraftMeta({
      [key]: [...(draftNormalizedMeta[key] || []), nextLine],
    });
    setActiveDraftSupplemental({
      kind: supplementalKind,
      id: nextLine.id,
    });
  };

  const removeDraftSupplementalLine = (supplementalKind) => {
    const key = supplementalKind === "note" ? "bottomNotes" : "topTitles";
    const lines = Array.isArray(draftNormalizedMeta[key])
      ? draftNormalizedMeta[key]
      : [];
    if (!lines.length) return;
    const activeMatches =
      activeDraftSupplemental.kind === supplementalKind &&
      lines.some((line) => line.id === activeDraftSupplemental.id);
    const targetId = activeMatches
      ? activeDraftSupplemental.id
      : lines[lines.length - 1].id;
    patchDraftMeta({
      [key]: lines.filter((line) => line.id !== targetId),
    });
    setActiveDraftSupplemental({ kind: "", id: "" });
  };

  const mathObjectChrome = !readOnly ? (
    <div
      {...{ [DOC_WRITER_MATH_EDITOR_ONLY_ATTR]: "true" }}
      aria-hidden="false"
      style={{
        // Treat Move + Edit as one balanced control pair. The rail itself is
        // centered over the Math presentation; 45% preserves the comfortable
        // separation of the existing controls without pushing either to an edge.
        position: "absolute",
        top: 8,
        left: "50%",
        transform: "translateX(-50%)",
        width: "45%",
        maxWidth: 380,
        minWidth: 0,
        zIndex: 20,
        height: 34,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        pointerEvents: "none",
      }}
    >
      <button
        type="button"
        {...{ [DOC_WRITER_MATH_EDITOR_ONLY_ATTR]: "true" }}
        data-docwriter-math-drag-handle="true"
        aria-label="Move mathematics"
        style={{
          ...buttonStyle,
          position: "relative",
          flex: "0 0 auto",
          width: 38,
          minWidth: 38,
          height: 34,
          minHeight: 34,
          padding: 0,
          borderRadius: 9,
          display: "grid",
          placeItems: "center",
          textAlign: "center",
          lineHeight: 1,
          cursor: "grab",
          touchAction: "none",
          fontSize: 17,
          userSelect: "none",
          pointerEvents: "auto",
          boxSizing: "border-box",
        }}
        onPointerDown={(event) => {
          if (event.button !== 0 || moveRef.current) return;
          event.preventDefault();
          event.stopPropagation();
          setResizeTooltip(null);

          // Movement is owned by DocCanvasEditor. Keep the Math component in an
          // interaction guard until pointer release so a parent refresh cannot
          // remount/default the object while the user is actively dragging it.
          moveRef.current = { pointerId: event.pointerId };
          notifyEditing(true, { operation: "move" });

          const finishMove = () => {
            window.removeEventListener("pointerup", finishMove, true);
            window.removeEventListener("pointercancel", finishMove, true);
            if (!moveRef.current) return;
            moveRef.current = null;
            notifyEditing(false, { operation: "move" });
          };

          window.addEventListener("pointerup", finishMove, true);
          window.addEventListener("pointercancel", finishMove, true);

          // The move operation never rewrites alignment, width, height, font,
          // source, or appearance. Only the Editor changes the block's document
          // position/order in response to this request.
          try {
            onRequestDrag?.(event, {
              blockId,
              value: serializedValue,
              meta: normalizedMeta,
              operation: "move",
            });
          } catch {}
        }}
      >
        <span
          aria-hidden="true"
          style={{
            display: "grid",
            placeItems: "center",
            width: "100%",
            height: "100%",
            lineHeight: 1,
            textAlign: "center",
            pointerEvents: "none",
          }}
        >
          ⠿
        </span>
      </button>

      <button
        type="button"
        {...{ [DOC_WRITER_MATH_EDITOR_ONLY_ATTR]: "true" }}
        aria-label="Edit mathematics"
        style={{
          ...buttonStyle,
          position: "relative",
          flex: "0 0 auto",
          height: 34,
          minHeight: 34,
          padding: "0 14px",
          display: "grid",
          placeItems: "center",
          boxSizing: "border-box",
          color: "#1E2A78",
          pointerEvents: "auto",
        }}
        onPointerDown={(event) => {
          event.preventDefault();
          event.stopPropagation();
          if (!isEditing) openEditor();
        }}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
        }}
      >
        Edit
      </button>
    </div>
  ) : null;

  const focusDraftSource = useCallback(() => {
    const input = draftSourceRef.current;
    if (!input) return;
    try { input.focus({ preventScroll: true }); } catch { try { input.focus(); } catch {} }
  }, []);

  const handleDraftSourceKeyDown = useCallback((event) => {
    // Keep ordinary editing keys completely local to the source editor. Dialog-local
    // Undo/Redo intentionally wins over browser/Canvas history while this editor is open.
    event.stopPropagation();
    const accelerator = event.ctrlKey || event.metaKey;
    const key = String(event.key || "").toLowerCase();
    if (accelerator && key === "z") {
      event.preventDefault();
      if (event.shiftKey) redoDraftChange();
      else undoDraftChange();
      return;
    }
    if (accelerator && key === "y") {
      event.preventDefault();
      redoDraftChange();
      return;
    }
    if (event.key !== "Tab") return;
    event.preventDefault();
    const input = event.currentTarget;
    const start = Number(input.selectionStart || 0);
    const end = Number(input.selectionEnd || start);
    const insertion = "  ";
    const currentRaw = String(draftStateRef.current?.raw ?? draftRaw);
    const next = `${currentRaw.slice(0, start)}${insertion}${currentRaw.slice(end)}`;
    updateDraftRaw(next);
    window.requestAnimationFrame(() => {
      try {
        input.focus({ preventScroll: true });
        input.setSelectionRange(start + insertion.length, start + insertion.length);
      } catch {}
    });
  }, [draftRaw, redoDraftChange, undoDraftChange, updateDraftRaw]);

  const draftPreviewResizeHandles = [
    { handle: "nw", glyph: "↖", cursor: "nwse-resize", vertical: "top", horizontal: "left" },
    { handle: "ne", glyph: "↗", cursor: "nesw-resize", vertical: "top", horizontal: "right" },
    { handle: "sw", glyph: "↙", cursor: "nesw-resize", vertical: "bottom", horizontal: "left" },
    { handle: "se", glyph: "↘", cursor: "nwse-resize", vertical: "bottom", horizontal: "right" },
  ];

  const draftPreviewEdgeResizeHandles = [
    { handle: "n", edge: "top", cursor: "ns-resize" },
    { handle: "s", edge: "bottom", cursor: "ns-resize" },
  ];

  const beginDraftResize = useCallback((event, handle = "se") => {
    if (!event || event.button !== 0 || draftResizeRef.current) return;
    const view = ownerDocument?.defaultView || (typeof window !== "undefined" ? window : null);
    if (!view) return;

    event.preventDefault();
    event.stopPropagation();
    setResizeTooltip(null);

    const current = snapshotDraftState();
    const startMeta = normalizeMathMeta(current.raw, current.meta);
    const surfaceRect = draftPreviewSurfaceRef.current?.getBoundingClientRect?.();
    const startWidth = clamp(
      Math.round(surfaceRect?.width || startMeta.widthPx || 420),
      DOC_WRITER_MATH_MIN_WIDTH_PX,
      DOC_WRITER_MATH_MAX_WIDTH_PX
    );
    const startHeight = clamp(
      Math.round(
        startMeta.heightPx > 0
          ? startMeta.heightPx
          : surfaceRect?.height || startMeta.minHeightPx || DOC_WRITER_MATH_MIN_HEIGHT_PX
      ),
      DOC_WRITER_MATH_MIN_HEIGHT_PX,
      DOC_WRITER_MATH_MAX_HEIGHT_PX
    );

    const resizeHorizontal = handle.includes("e") || handle.includes("w");
    const resizeFromWest = handle.includes("w");
    const resizeFromNorth = handle.includes("n");
    const resizeFromTopEdge = handle === "n";
    const startTopEdgeInset = clamp(
      finiteNumber(startMeta.topEdgeInsetPx, 0),
      0,
      DOC_WRITER_MATH_MAX_HEIGHT_PX
    );

    const state = {
      handle,
      startX: event.clientX,
      startY: event.clientY,
      startWidth,
      startHeight,
      startTopEdgeInset,
      startMeta,
      raw: current.raw,
      historyCaptured: false,
    };
    draftResizeRef.current = state;
    try { event.currentTarget.setPointerCapture?.(event.pointerId); } catch {}

    const move = (moveEvent) => {
      if (!draftResizeRef.current) return;
      const deltaX = moveEvent.clientX - state.startX;
      const deltaY = moveEvent.clientY - state.startY;
      if (!deltaX && !deltaY) return;
      if (!state.historyCaptured) {
        captureDraftCheckpoint();
        state.historyCaptured = true;
      }

      const nextWidth = resizeHorizontal
        ? clamp(
            state.startWidth + (resizeFromWest ? -deltaX : deltaX),
            DOC_WRITER_MATH_MIN_WIDTH_PX,
            DOC_WRITER_MATH_MAX_WIDTH_PX
          )
        : state.startWidth;
      const nextHeight = clamp(
        state.startHeight + (resizeFromNorth ? -deltaY : deltaY),
        DOC_WRITER_MATH_MIN_HEIGHT_PX,
        DOC_WRITER_MATH_MAX_HEIGHT_PX
      );
      const actualHeightDelta = state.startHeight - nextHeight;
      const nextTopEdgeInset = resizeFromTopEdge
        ? clamp(
            state.startTopEdgeInset + actualHeightDelta,
            0,
            DOC_WRITER_MATH_MAX_HEIGHT_PX
          )
        : state.startTopEdgeInset;

      const nextMeta = {
        ...state.startMeta,
        widthMode: resizeHorizontal ? "custom" : state.startMeta.widthMode,
        widthPx: resizeHorizontal ? Math.round(nextWidth) : state.startMeta.widthPx,
        heightPx: Math.round(nextHeight),
        minHeightPx: 0,
        topEdgeInsetPx: Math.round(nextTopEdgeInset),
      };
      draftStateRef.current = { raw: state.raw, meta: nextMeta };
      setDraftMeta(nextMeta);
    };

    const finish = () => {
      view.removeEventListener("pointermove", move, true);
      view.removeEventListener("pointerup", finish, true);
      view.removeEventListener("pointercancel", finish, true);
      draftResizeRef.current = null;
    };

    view.addEventListener("pointermove", move, true);
    view.addEventListener("pointerup", finish, true);
    view.addEventListener("pointercancel", finish, true);
  }, [captureDraftCheckpoint, ownerDocument, snapshotDraftState]);

  const editDialog = !readOnly && isEditing && ownerDocument?.body
    ? createPortal(
        <div
          className="docwriter-math-edit-overlay"
          {...{ [DOC_WRITER_MATH_EDITOR_ONLY_ATTR]: "true" }}
          onPointerDown={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => {
            event.stopPropagation();
            const accelerator = event.ctrlKey || event.metaKey;
            const key = String(event.key || "").toLowerCase();
            if (accelerator && key === "z") {
              event.preventDefault();
              if (event.shiftKey) redoDraftChange();
              else undoDraftChange();
              return;
            }
            if (accelerator && key === "y") {
              event.preventDefault();
              redoDraftChange();
              return;
            }
            if (event.key === "Escape") {
              event.preventDefault();
              if (deleteConfirmOpen) setDeleteConfirmOpen(false);
              else closeEditor();
            }
          }}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 2147483000,
            display: "grid",
            placeItems: "center",
            padding: 20,
            boxSizing: "border-box",
            background: "rgba(15,23,42,.30)",
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Edit mathematics"
            onPointerDown={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
            style={{
              width: "min(1180px, 96vw)",
              maxHeight: "90vh",
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              boxSizing: "border-box",
              borderRadius: 18,
              border: "1px solid rgba(148,163,184,.35)",
              background: PANEL_BG,
              color: TEXT_DARK,
              boxShadow: "0 24px 70px rgba(15,23,42,.28)",
              fontFamily: 'Arial, Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
            }}
          >
            <div style={{ flex: "0 0 auto", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: "17px 18px 15px", borderBottom: "1px solid rgba(148,163,184,.22)", background: "#ffffff" }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 900 }}>Edit mathematics</div>
                <div style={{ marginTop: 3, color: TEXT_MUTED, fontSize: 12 }}>
                  Edit the source, presentation, and equation position. The document math changes only when you choose Save changes.
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 7, flex: "0 0 auto" }}>
                <button
                  type="button"
                  style={{ ...buttonStyle, minWidth: 66, opacity: draftUndoStack.length ? 1 : 0.48, cursor: draftUndoStack.length ? "pointer" : "default" }}
                  disabled={!draftUndoStack.length}
                  onClick={undoDraftChange}
                  title="Undo the last unsaved math-preview change"
                >
                  Undo
                </button>
                <button
                  type="button"
                  style={{ ...buttonStyle, minWidth: 66, opacity: draftRedoStack.length ? 1 : 0.48, cursor: draftRedoStack.length ? "pointer" : "default" }}
                  disabled={!draftRedoStack.length}
                  onClick={redoDraftChange}
                  title="Redo the last unsaved math-preview change"
                >
                  Redo
                </button>
                <button type="button" style={buttonStyle} onClick={closeEditor}>Close</button>
              </div>
            </div>

            <div style={{ flex: "1 1 auto", minHeight: 0, overflowY: "auto", overflowX: "hidden", scrollbarGutter: "stable", padding: "16px 18px 18px", background: "#ffffff" }}>
              <div style={{ display: "grid", gridTemplateColumns: "minmax(310px, 390px) minmax(0, 1fr)", gap: 18, alignItems: "start" }}>
                <div style={{ minWidth: 0, display: "grid", gap: 12 }}>
                  <section style={sectionStyle}>
                    <div style={sectionTitleStyle}>Placement and size</div>
                    <div style={rowGrid}>
                      <label style={labelStyle}>
                        Alignment
                        <select value={draftNormalizedMeta.alignment} onChange={(event) => patchDraftMeta({ alignment: event.target.value })} style={inputStyle}>
                          <option value="left">Left</option>
                          <option value="center">Center</option>
                          <option value="right">Right</option>
                        </select>
                      </label>
                      <label style={labelStyle}>
                        Width preset
                        <select
                          value={draftNormalizedMeta.widthMode}
                          onChange={(event) => {
                            const widthMode = event.target.value;
                            patchDraftMeta({
                              widthMode,
                              widthPx: widthMode === "custom" ? draftNormalizedMeta.widthPx : presetWidthPx(widthMode),
                            });
                          }}
                          style={inputStyle}
                        >
                          {Object.entries(WIDTH_MODES).map(([key, config]) => <option key={key} value={key}>{config.label}</option>)}
                          <option value="custom">Custom width</option>
                        </select>
                      </label>
                      <label style={labelStyle}>
                        Width (px)
                        <DraftNumberInput
                          value={draftNormalizedMeta.widthPx}
                          fallback={760}
                          min={DOC_WRITER_MATH_MIN_WIDTH_PX}
                          max={DOC_WRITER_MATH_MAX_WIDTH_PX}
                          onCommit={(next) => patchDraftMeta({ widthMode: "custom", widthPx: next })}
                          style={inputStyle}
                          ariaLabel="Math width in pixels"
                        />
                      </label>
                      <label style={labelStyle}>
                        Height (px, 0 = auto)
                        <DraftNumberInput
                          value={draftNormalizedMeta.heightPx}
                          fallback={0}
                          min={0}
                          max={DOC_WRITER_MATH_MAX_HEIGHT_PX}
                          onCommit={(next) => patchDraftMeta({ heightPx: next, minHeightPx: 0 })}
                          style={inputStyle}
                          ariaLabel="Math height in pixels; zero uses automatic height"
                        />
                      </label>
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 9 }}>
                      {["small", "medium", "large"].map((sizeKey) => (
                        <button
                          key={sizeKey}
                          type="button"
                          style={buttonStyle}
                          onClick={() => patchDraftMeta({ size: sizeKey, fontSizePx: presetFontSizePx(sizeKey) })}
                        >
                          {SIZE_CONFIG[sizeKey].label}
                        </button>
                      ))}
                    </div>
                  </section>

                  <section style={sectionStyle}>
                    <div style={sectionTitleStyle}>Equation typography</div>
                    <div style={rowGrid}>
                      <label style={labelStyle}>
                        Font size (px)
                        <DraftNumberInput
                          value={draftNormalizedMeta.fontSizePx}
                          fallback={15}
                          min={6}
                          max={72}
                          onCommit={(next) => patchDraftMeta({ fontSizePx: next })}
                          style={inputStyle}
                          ariaLabel="Math font size in pixels"
                        />
                      </label>
                      <label style={labelStyle}>
                        Line height
                        <DraftNumberInput
                          value={draftNormalizedMeta.lineHeight}
                          fallback={1.45}
                          min={0.9}
                          max={3}
                          step={0.05}
                          onCommit={(next) => patchDraftMeta({ lineHeight: next })}
                          style={inputStyle}
                          ariaLabel="Math line height"
                        />
                      </label>
                      <label style={labelStyle}>
                        Line gap (px)
                        <DraftNumberInput
                          value={draftNormalizedMeta.lineGapPx}
                          fallback={8}
                          min={0}
                          max={60}
                          onCommit={(next) => patchDraftMeta({ lineGapPx: next })}
                          style={inputStyle}
                          ariaLabel="Math line gap in pixels"
                        />
                      </label>
                      <ColorControl label="Equation color" value={draftNormalizedMeta.mathColor} fallback={TEXT_DARK} onChange={(next) => patchDraftMeta({ mathColor: next })} inputStyle={inputStyle} labelStyle={labelStyle} />
                    </div>
                  </section>

                  <section style={sectionStyle}>
                    <div style={sectionTitleStyle}>Background and border</div>
                    <div style={rowGrid}>
                      <label style={labelStyle}>
                        Appearance preset
                        <select value={draftNormalizedMeta.screenVariant} onChange={(event) => patchDraftMeta({ screenVariant: event.target.value })} style={inputStyle}>
                          {Object.entries(SCREEN_VARIANTS).map(([key, config]) => <option key={key} value={key}>{config.label}</option>)}
                        </select>
                      </label>
                      <label style={labelStyle}>
                        Shadow
                        <select value={draftNormalizedMeta.shadow} onChange={(event) => patchDraftMeta({ shadow: event.target.value })} style={inputStyle}>
                          <option value="none">None</option>
                          <option value="soft">Soft</option>
                          <option value="strong">Strong</option>
                        </select>
                      </label>
                      <ColorControl label="Background color" value={draftNormalizedMeta.backgroundColor || resolvedSurfaceColors(draftNormalizedMeta).background} fallback="#ffffff" onChange={(next) => patchDraftMeta({ backgroundColor: next })} inputStyle={inputStyle} labelStyle={labelStyle} />
                      <ColorControl label="Border color" value={draftNormalizedMeta.borderColor || resolvedSurfaceColors(draftNormalizedMeta).borderColor} fallback="#cbd5e1" onChange={(next) => patchDraftMeta({ borderColor: next })} inputStyle={inputStyle} labelStyle={labelStyle} />
                      <label style={labelStyle}>
                        Border width (px)
                        <DraftNumberInput value={draftNormalizedMeta.borderWidthPx} fallback={1} min={0} max={12} onCommit={(next) => patchDraftMeta({ borderWidthPx: next })} style={inputStyle} ariaLabel="Math border width" />
                      </label>
                      <label style={labelStyle}>
                        Corner radius (px)
                        <DraftNumberInput value={draftNormalizedMeta.borderRadiusPx} fallback={10} min={0} max={40} onCommit={(next) => patchDraftMeta({ borderRadiusPx: next })} style={inputStyle} ariaLabel="Math corner radius" />
                      </label>
                    </div>
                  </section>

                  <section style={sectionStyle}>
                    <div style={sectionTitleStyle}>Spacing</div>
                    <div style={rowGrid}>
                      <label style={labelStyle}>
                        Horizontal padding (px)
                        <DraftNumberInput value={draftNormalizedMeta.paddingX} fallback={16} min={0} max={80} onCommit={(next) => patchDraftMeta({ paddingX: next })} style={inputStyle} ariaLabel="Math horizontal padding" />
                      </label>
                      <label style={labelStyle}>
                        Vertical padding (px)
                        <DraftNumberInput value={draftNormalizedMeta.paddingY} fallback={14} min={0} max={80} onCommit={(next) => patchDraftMeta({ paddingY: next })} style={inputStyle} ariaLabel="Math vertical padding" />
                      </label>
                    </div>
                  </section>

                  <section style={sectionStyle}>
                    <div style={sectionTitleStyle}>Labels and description</div>
                    <div style={{ display: "grid", gap: 9 }}>
                      <label style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12, color: TEXT_DARK }}>
                        <input type="checkbox" checked={draftNormalizedMeta.showKindLabel} onChange={(event) => patchDraftMeta({ showKindLabel: event.target.checked })} />
                        Show subject label
                      </label>
                      <label style={labelStyle}>
                        Subject label text
                        <input
                          type="text"
                          value={draftNormalizedMeta.kindLabelText}
                          placeholder={detectMathKind(normalizeContent(draftRaw))}
                          onChange={(event) => patchDraftMeta({ kindLabelText: event.target.value })}
                          style={inputStyle}
                        />
                      </label>
                      <label style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12, color: TEXT_DARK }}>
                        <input type="checkbox" checked={draftNormalizedMeta.showSummary} onChange={(event) => patchDraftMeta({ showSummary: event.target.checked })} />
                        Show short description
                      </label>
                      <label style={labelStyle}>
                        Short description text
                        <input
                          type="text"
                          value={draftNormalizedMeta.summaryText}
                          placeholder={buildReadableSummary(
                            detectMathKind(normalizeContent(draftRaw)),
                            normalizeContent(draftRaw)
                          ) || "Type a short description"
                          }
                          onChange={(event) => patchDraftMeta({ summaryText: event.target.value })}
                          style={inputStyle}
                        />
                      </label>
                      <div style={rowGrid}>
                        <ColorControl label="Subject label color" value={draftNormalizedMeta.kindLabelColor} fallback={TEXT_MUTED} onChange={(next) => patchDraftMeta({ kindLabelColor: next })} inputStyle={inputStyle} labelStyle={labelStyle} />
                        <ColorControl label="Description color" value={draftNormalizedMeta.summaryColor} fallback={TEXT_MUTED} onChange={(next) => patchDraftMeta({ summaryColor: next })} inputStyle={inputStyle} labelStyle={labelStyle} />
                      </div>
                    </div>
                  </section>

                  <section style={sectionStyle}>
                    <div style={sectionTitleStyle}>Top titles and bottom notes</div>
                    <div style={{ fontSize: 11, lineHeight: 1.45, color: TEXT_MUTED, marginBottom: 9 }}>
                      Add as many independent lines as needed. Click a title/note line in the preview to make it active, then use the formatting controls below. These remain draft-only until Save changes.
                    </div>
                    <div style={{ ...rowGrid, marginBottom: 9 }}>
                      <button type="button" style={buttonStyle} onClick={() => addDraftSupplementalLine("title")}>
                        Add top title line
                      </button>
                      <button type="button" style={buttonStyle} onClick={() => removeDraftSupplementalLine("title")}>
                        Remove active top line
                      </button>
                      <button type="button" style={buttonStyle} onClick={() => addDraftSupplementalLine("note")}>
                        Add bottom note line
                      </button>
                      <button type="button" style={buttonStyle} onClick={() => removeDraftSupplementalLine("note")}>
                        Remove active bottom line
                      </button>
                    </div>
                    <div style={{ fontSize: 11, color: activeDraftSupplementalLine ? TEXT_DARK : TEXT_MUTED, marginBottom: 9 }}>
                      {activeDraftSupplementalLine
                        ? `${activeDraftSupplemental.kind === "note" ? "Bottom note" : "Top title"} line selected. Type directly in the preview or format it here.`
                        : "Click a top title or bottom note line to format it."}
                    </div>
                    <div style={rowGrid}>
                      <label style={labelStyle}>
                        Active line alignment
                        <select
                          value={activeDraftSupplementalLine?.alignment || "center"}
                          disabled={!activeDraftSupplementalLine}
                          onChange={(event) => {
                            if (!activeDraftSupplementalLine) return;
                            updateDraftSupplementalLine(
                              activeDraftSupplemental.kind,
                              activeDraftSupplementalLine.id,
                              { alignment: event.target.value }
                            );
                          }}
                          style={{ ...inputStyle, opacity: activeDraftSupplementalLine ? 1 : 0.55 }}
                        >
                          <option value="left">Left</option>
                          <option value="center">Center</option>
                          <option value="right">Right</option>
                        </select>
                      </label>
                      <label style={labelStyle}>
                        Active line font size (px)
                        <DraftNumberInput
                          value={activeDraftSupplementalLine?.fontSizePx ?? 14}
                          fallback={14}
                          min={3}
                          max={96}
                          onCommit={(next) => {
                            if (!activeDraftSupplementalLine) return;
                            updateDraftSupplementalLine(
                              activeDraftSupplemental.kind,
                              activeDraftSupplementalLine.id,
                              { fontSizePx: next }
                            );
                          }}
                          style={{ ...inputStyle, opacity: activeDraftSupplementalLine ? 1 : 0.55 }}
                          ariaLabel="Active title or note line font size"
                        />
                      </label>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 8, marginTop: 9 }}>
                      {[
                        ["Bold", "bold"],
                        ["Italic", "italic"],
                        ["Underline", "underline"],
                      ].map(([label, property]) => {
                        const active = Boolean(activeDraftSupplementalLine?.[property]);
                        return (
                          <button
                            key={`math-supplemental-${property}`}
                            type="button"
                            disabled={!activeDraftSupplementalLine}
                            style={{
                              ...buttonStyle,
                              opacity: activeDraftSupplementalLine ? 1 : 0.55,
                              background: active ? "#eef2ff" : buttonStyle.background,
                              borderColor: active ? "#a5b4fc" : "rgba(15,23,42,.15)",
                              color: active ? "#1E2A78" : buttonStyle.color,
                            }}
                            onClick={() => {
                              if (!activeDraftSupplementalLine) return;
                              updateDraftSupplementalLine(
                                activeDraftSupplemental.kind,
                                activeDraftSupplementalLine.id,
                                { [property]: !active }
                              );
                            }}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  </section>

                  <section style={sectionStyle}>
                    <div style={sectionTitleStyle}>Equation position inside the math box</div>
                    <div style={rowGrid}>
                      <label style={labelStyle}>
                        X offset (px)
                        <DraftNumberInput value={draftNormalizedMeta.contentOffsetX} fallback={0} min={-800} max={800} onCommit={(next) => patchDraftMeta({ contentOffsetX: next })} style={inputStyle} ariaLabel="Equation horizontal offset" />
                      </label>
                      <label style={labelStyle}>
                        Y offset (px)
                        <DraftNumberInput value={draftNormalizedMeta.contentOffsetY} fallback={0} min={-800} max={800} onCommit={(next) => patchDraftMeta({ contentOffsetY: next })} style={inputStyle} ariaLabel="Equation vertical offset" />
                      </label>
                    </div>
                    <div style={{ marginTop: 9, display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                      <button type="button" style={buttonStyle} onClick={() => patchDraftMeta({ contentOffsetX: 0, contentOffsetY: 0 })}>Reset equation position</button>
                      <span style={{ fontSize: 11, color: TEXT_MUTED }}>You can also drag the rendered equation directly in the live preview.</span>
                    </div>
                  </section>
                </div>

                <div style={{ minWidth: 0, position: "sticky", top: 0, alignSelf: "start" }}>
                  <div style={{ fontSize: 12, fontWeight: 900, color: TEXT_DARK, marginBottom: 6 }}>Live preview</div>
                  <div style={{ fontSize: 11, color: TEXT_MUTED, lineHeight: 1.45, marginBottom: 9 }}>
                    Edit directly in the source box below. Space, Backspace, Delete, Enter, and Tab work normally. Ordinary prose keeps its spaces while equation lines continue through the math renderer. Click the rendered preview to return focus to the source; drag it to reposition the equation. The preview gets its own scrollbar if the math grows.
                  </div>
                  <div
                    className="docwriter-math-live-preview-scroll"
                    style={{
                      position: "relative",
                      maxHeight: "min(64vh, 650px)",
                      minHeight: 330,
                      overflowY: "auto",
                      overflowX: "auto",
                      scrollbarGutter: "stable",
                      padding: "14px 20px 26px 14px",
                      border: "1px solid #dbe3ee",
                      borderRadius: 12,
                      background: "#f8fafc",
                      boxSizing: "border-box",
                    }}
                  >
                    <label style={{ ...labelStyle, marginBottom: 12 }}>
                      Math source / equation block
                      <textarea
                        ref={draftSourceRef}
                        value={draftRaw}
                        onChange={(event) => updateDraftRaw(event.target.value)}
                        onKeyDown={handleDraftSourceKeyDown}
                        onMouseDown={(event) => event.stopPropagation()}
                        onPointerDown={(event) => event.stopPropagation()}
                        spellCheck={false}
                        rows={9}
                        style={{ ...inputStyle, minHeight: 175, resize: "vertical", fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace", lineHeight: 1.5, tabSize: 2, whiteSpace: "pre" }}
                        placeholder={'Gross Profit = Revenue - Cost of Goods Sold\n\\[ R(t)=60,000+1,500t+180t^2 \\]\n\\[ \\int_0^{12} R(t) \\, dt \\]'}
                      />
                    </label>
                    <div style={{ minWidth: "100%", width: "max-content", padding: 8, boxSizing: "border-box" }}>
                      <div
                        style={{
                          position: "relative",
                          display: "flow-root",
                          width: draftWidthStyle,
                          maxWidth: "100%",
                          minWidth: draftNormalizedMeta.widthMode === "custom" ? `${Math.min(draftNormalizedMeta.widthPx, DOC_WRITER_MATH_MAX_WIDTH_PX)}px` : undefined,
                          marginLeft: draftNormalizedMeta.alignment === "left" ? 0 : draftNormalizedMeta.alignment === "right" ? "auto" : "auto",
                          marginRight: draftNormalizedMeta.alignment === "right" ? 0 : draftNormalizedMeta.alignment === "left" ? "auto" : "auto",
                        }}
                      >
                        {draftPreviewResizeHandles.map((handle) => {
                          const isHovered = resizeTooltip?.handle === `preview-${handle.handle}`;
                          const verticalStyle = handle.vertical === "top"
                            ? { top: draftNormalizedMeta.topEdgeInsetPx + 4 }
                            : { bottom: 4 };
                          const horizontalStyle = handle.horizontal === "left" ? { left: 4 } : { right: 4 };
                          return (
                            <div
                              key={`math-preview-resize-${handle.handle}`}
                              role="button"
                              tabIndex={-1}
                              {...{ [DOC_WRITER_MATH_EDITOR_ONLY_ATTR]: "true" }}
                              data-docwriter-math-preview-resize-corner={handle.handle}
                              aria-label={`Resize preview mathematics from ${handle.vertical} ${handle.horizontal} corner`}
                              title="Resize mathematics"
                              onPointerEnter={(event) => updateResizeTooltip(event, `preview-${handle.handle}`, "corner")}
                              onPointerMove={(event) => updateResizeTooltip(event, `preview-${handle.handle}`, "corner")}
                              onPointerLeave={() => setResizeTooltip(null)}
                              onPointerDown={(event) => beginDraftResize(event, handle.handle)}
                              style={{
                                position: "absolute",
                                ...verticalStyle,
                                ...horizontalStyle,
                                width: DOC_WRITER_MATH_RESIZE_HANDLE_PX,
                                height: DOC_WRITER_MATH_RESIZE_HANDLE_PX,
                                zIndex: 24,
                                cursor: handle.cursor,
                                touchAction: "none",
                                display: "grid",
                                placeItems: "center",
                                borderRadius: 7,
                                background: isHovered ? "rgba(255,255,255,0.98)" : "rgba(255,255,255,0.88)",
                                border: isHovered ? "1px solid rgba(30,42,120,0.34)" : "1px solid rgba(30,42,120,0.12)",
                                color: "rgba(30,42,120,0.88)",
                                fontSize: 15,
                                lineHeight: 1,
                                userSelect: "none",
                                boxSizing: "border-box",
                              }}
                            >
                              <span aria-hidden="true" style={{ pointerEvents: "none", lineHeight: 1 }}>
                                {handle.glyph}
                              </span>
                            </div>
                          );
                        })}

                        {draftPreviewEdgeResizeHandles.map((handle) => {
                          const isHovered = resizeTooltip?.handle === `preview-${handle.handle}`;
                          return (
                            <div
                              key={`math-preview-resize-edge-${handle.handle}`}
                              role="button"
                              tabIndex={-1}
                              {...{ [DOC_WRITER_MATH_EDITOR_ONLY_ATTR]: "true" }}
                              data-docwriter-math-preview-resize-edge={handle.edge}
                              aria-label={`Resize preview mathematics height from ${handle.edge} edge`}
                              title="Resize height"
                              onPointerEnter={(event) => updateResizeTooltip(event, `preview-${handle.handle}`, "edge")}
                              onPointerMove={(event) => updateResizeTooltip(event, `preview-${handle.handle}`, "edge")}
                              onPointerLeave={() => setResizeTooltip(null)}
                              onPointerDown={(event) => beginDraftResize(event, handle.handle)}
                              style={{
                                position: "absolute",
                                left: DOC_WRITER_MATH_RESIZE_HANDLE_PX + 8,
                                right: DOC_WRITER_MATH_RESIZE_HANDLE_PX + 8,
                                ...(handle.edge === "top"
                                  ? { top: draftNormalizedMeta.topEdgeInsetPx }
                                  : { bottom: 0 }),
                                height: 14,
                                zIndex: 30,
                                cursor: handle.cursor,
                                touchAction: "none",
                                userSelect: "none",
                                boxSizing: "border-box",
                                background: "transparent",
                                pointerEvents: "auto",
                              }}
                            >
                              <span
                                aria-hidden="true"
                                style={{
                                  position: "absolute",
                                  left: "50%",
                                  transform: "translateX(-50%)",
                                  ...(handle.edge === "top" ? { top: 0 } : { bottom: 0 }),
                                  width: isHovered ? 54 : 34,
                                  height: 2,
                                  borderRadius: 999,
                                  background: isHovered ? "rgba(30,42,120,0.62)" : "rgba(30,42,120,0.20)",
                                  pointerEvents: "none",
                                  transition: "width 100ms ease, background 100ms ease",
                                }}
                              />
                            </div>
                          );
                        })}

                        <MathSurface
                          rawValue={draftRaw}
                          metaValue={draftNormalizedMeta}
                          interactive
                          onMetaPatch={patchDraftMeta}
                          surfaceRef={draftPreviewSurfaceRef}
                          onActivate={focusDraftSource}
                          supplementalEditable
                          onSupplementalFocus={(supplementalKind, lineId) =>
                            setActiveDraftSupplemental({ kind: supplementalKind, id: lineId })
                          }
                          onSupplementalCommit={(supplementalKind, lineId, nextText) =>
                            updateDraftSupplementalLine(supplementalKind, lineId, { text: nextText })
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div style={{ flex: "0 0 auto", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "13px 18px 15px", borderTop: "1px solid rgba(148,163,184,.22)", background: "#ffffff" }}>
              <button type="button" style={{ ...buttonStyle, color: "#b91c1c", borderColor: "#fecaca", background: "#fff7f7" }} onClick={requestDeleteEquation}>
                Delete equation
              </button>
              <div style={{ display: "flex", gap: 8 }}>
                <button type="button" style={buttonStyle} onClick={closeEditor}>Cancel</button>
                <button type="button" style={{ ...buttonStyle, borderColor: "#1E2A78", background: "#1E2A78", color: "#fff" }} onClick={saveEditor}>
                  Save changes
                </button>
              </div>
            </div>
          </div>

          {deleteConfirmOpen ? (
            <div
              {...{ [DOC_WRITER_MATH_EDITOR_ONLY_ATTR]: "true" }}
              role="presentation"
              onPointerDown={(event) => {
                event.stopPropagation();
                if (event.target === event.currentTarget) setDeleteConfirmOpen(false);
              }}
              onMouseDown={(event) => event.stopPropagation()}
              onClick={(event) => event.stopPropagation()}
              style={{
                position: "fixed",
                inset: 0,
                zIndex: 2147483100,
                display: "grid",
                placeItems: "center",
                padding: 20,
                boxSizing: "border-box",
                background: "rgba(15,23,42,0.38)",
              }}
            >
              <div
                role="alertdialog"
                aria-modal="true"
                aria-label="Confirm delete equation"
                onPointerDown={(event) => event.stopPropagation()}
                onClick={(event) => event.stopPropagation()}
                style={{
                  width: "min(420px, calc(100vw - 40px))",
                  padding: 20,
                  borderRadius: 14,
                  border: "1px solid rgba(148,163,184,0.35)",
                  background: "#ffffff",
                  boxShadow: "0 24px 70px rgba(15,23,42,0.30)",
                  color: TEXT_DARK,
                  boxSizing: "border-box",
                }}
              >
                <div style={{ fontSize: 16, fontWeight: 900, lineHeight: 1.25 }}>Delete equation?</div>
                <div style={{ marginTop: 7, fontSize: 12, lineHeight: 1.5, color: TEXT_MUTED }}>
                  This will remove the equation from the document. This action can be undone from the Canvas after the delete is committed.
                </div>
                <div style={{ marginTop: 18, display: "flex", justifyContent: "flex-end", gap: 8 }}>
                  <button type="button" style={buttonStyle} onClick={() => setDeleteConfirmOpen(false)}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    style={{ ...buttonStyle, background: "#b91c1c", borderColor: "#b91c1c", color: "#ffffff" }}
                    onClick={deleteEquation}
                  >
                    Delete equation
                  </button>
                </div>
              </div>
            </div>
          ) : null}
        </div>,
        ownerDocument.body
      )
    : null;

  const resizeHandles = [
    { handle: "nw", glyph: "↖", cursor: "nwse-resize", vertical: "top", horizontal: "left" },
    { handle: "ne", glyph: "↗", cursor: "nesw-resize", vertical: "top", horizontal: "right" },
    { handle: "sw", glyph: "↙", cursor: "nesw-resize", vertical: "bottom", horizontal: "left" },
    { handle: "se", glyph: "↘", cursor: "nwse-resize", vertical: "bottom", horizontal: "right" },
  ];

  const edgeResizeHandles = [
    { handle: "n", edge: "top", cursor: "ns-resize" },
    { handle: "s", edge: "bottom", cursor: "ns-resize" },
  ];

  const updateResizeTooltip = (event, handle, kind = "corner") => {
    if (!event) return;
    setResizeTooltip({
      handle,
      kind,
      x: Number(event.clientX || 0),
      y: Number(event.clientY || 0),
    });
  };

  const resizeTooltipPortal =
    !readOnly && resizeTooltip && ownerDocument?.body
      ? (() => {
          const view = ownerDocument.defaultView;
          const viewportWidth = Number(view?.innerWidth || 1200);
          const viewportHeight = Number(view?.innerHeight || 800);
          const tooltipWidth = 146;
          const tooltipHeight = 28;
          const left = clamp(
            resizeTooltip.x - tooltipWidth - 12,
            8,
            Math.max(8, viewportWidth - tooltipWidth - 8)
          );
          const top = clamp(
            resizeTooltip.y - tooltipHeight - 10,
            8,
            Math.max(8, viewportHeight - tooltipHeight - 8)
          );

          return createPortal(
            <div
              {...{ [DOC_WRITER_MATH_EDITOR_ONLY_ATTR]: "true" }}
              role="tooltip"
              style={{
                position: "fixed",
                left,
                top,
                zIndex: 2147483100,
                width: tooltipWidth,
                minHeight: tooltipHeight,
                padding: "6px 9px",
                boxSizing: "border-box",
                borderRadius: 7,
                background: "#0f172a",
                color: "#ffffff",
                fontSize: 10,
                lineHeight: 1.35,
                fontWeight: 800,
                textAlign: "center",
                whiteSpace: "nowrap",
                pointerEvents: "none",
                boxShadow: "0 5px 14px rgba(15,23,42,0.18)",
              }}
            >
              {resizeTooltip.kind === "edge" ? "Resize height" : "Resize mathematics"}
            </div>,
            ownerDocument.body
          );
        })()
      : null;

  return (
    <div
      ref={rootRef}
      className={`docwriter-math-shell ${className}`.trim()}
      data-docwriter-math="true"
      data-docwriter-math-version={DOC_WRITER_MATH_COMPONENT_VERSION}
      data-docwriter-math-kind={kind}
      data-docwriter-math-align={normalizedMeta.alignment}
      data-docwriter-math-size={normalizedMeta.size}
      data-docwriter-math-width-mode={normalizedMeta.widthMode}
      data-docwriter-math-block-id={blockId || ""}
      data-docwriter-math-screen-variant={normalizedMeta.screenVariant}
      style={shellStyle}
    >
      {mathObjectChrome}

      <div
        className="docwriter-math-canvas-presentation"
        data-docwriter-math-canvas-presentation="true"
        style={{
          position: "relative",
          display: "flow-root",
          width: "100%",
          minWidth: 0,
          boxSizing: "border-box",
          pointerEvents: "auto",
        }}
      >
      {!readOnly
        ? resizeHandles.map((handle) => {
            const isHovered = resizeTooltip?.handle === handle.handle;
            const verticalStyle =
              handle.vertical === "top"
                ? { top: normalizedMeta.topEdgeInsetPx + 4 }
                : { bottom: 4 };
            const horizontalStyle =
              handle.horizontal === "left" ? { left: 4 } : { right: 4 };

            return (
              <div
                key={`math-resize-${handle.handle}`}
                role="button"
                tabIndex={-1}
                {...{ [DOC_WRITER_MATH_EDITOR_ONLY_ATTR]: "true" }}
                data-docwriter-math-resize-handle="true"
                data-docwriter-math-resize-corner={handle.handle}
                aria-label={`Resize mathematics from ${handle.vertical} ${handle.horizontal} corner`}
                onPointerEnter={(event) => updateResizeTooltip(event, handle.handle, "corner")}
                onPointerMove={(event) => updateResizeTooltip(event, handle.handle, "corner")}
                onPointerLeave={() => setResizeTooltip(null)}
                onPointerDown={(event) => beginResize(event, handle.handle)}
                style={{
                  position: "absolute",
                  ...verticalStyle,
                  ...horizontalStyle,
                  width: DOC_WRITER_MATH_RESIZE_HANDLE_PX,
                  height: DOC_WRITER_MATH_RESIZE_HANDLE_PX,
                  zIndex: 24,
                  cursor: handle.cursor,
                  touchAction: "none",
                  display: "grid",
                  placeItems: "center",
                  borderRadius: 7,
                  background: isHovered
                    ? "rgba(255,255,255,0.98)"
                    : "rgba(255,255,255,0.74)",
                  border: isHovered
                    ? "1px solid rgba(30,42,120,0.34)"
                    : "1px solid rgba(30,42,120,0.10)",
                  color: "rgba(30,42,120,0.88)",
                  fontSize: 15,
                  lineHeight: 1,
                  userSelect: "none",
                  boxSizing: "border-box",
                }}
              >
                <span aria-hidden="true" style={{ pointerEvents: "none", lineHeight: 1 }}>
                  {handle.glyph}
                </span>
              </div>
            );
          })
        : null}

      {!readOnly
        ? edgeResizeHandles.map((handle) => {
            const isHovered =
              resizeTooltip?.handle === handle.handle && resizeTooltip?.kind === "edge";

            return (
              <div
                key={`math-resize-edge-${handle.handle}`}
                role="button"
                tabIndex={-1}
                {...{ [DOC_WRITER_MATH_EDITOR_ONLY_ATTR]: "true" }}
                data-docwriter-math-resize-handle="true"
                data-docwriter-math-resize-edge={handle.edge}
                aria-label={`Resize mathematics height from ${handle.edge} edge`}
                onPointerEnter={(event) => updateResizeTooltip(event, handle.handle, "edge")}
                onPointerMove={(event) => updateResizeTooltip(event, handle.handle, "edge")}
                onPointerLeave={() => setResizeTooltip(null)}
                onPointerDown={(event) => beginResize(event, handle.handle)}
                style={{
                  position: "absolute",
                  left: DOC_WRITER_MATH_RESIZE_HANDLE_PX + 8,
                  right: DOC_WRITER_MATH_RESIZE_HANDLE_PX + 8,
                  ...(handle.edge === "top"
                    ? { top: normalizedMeta.topEdgeInsetPx }
                    : { bottom: 0 }),
                  height: 14,
                  zIndex: 30,
                  cursor: handle.cursor,
                  touchAction: "none",
                  userSelect: "none",
                  boxSizing: "border-box",
                  background: "transparent",
                  pointerEvents: "auto",
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    left: "50%",
                    transform: "translateX(-50%)",
                    ...(handle.edge === "top"
                      ? { top: 0 }
                      : { bottom: 0 }),
                    width: isHovered ? 54 : 34,
                    height: 2,
                    borderRadius: 999,
                    background: isHovered
                      ? "rgba(30,42,120,0.62)"
                      : "rgba(30,42,120,0.16)",
                    pointerEvents: "none",
                    transition: "width 100ms ease, background 100ms ease",
                  }}
                />
              </div>
            );
          })
        : null}

      <MathSurface
        rawValue={raw}
        metaValue={normalizedMeta}
        surfaceRef={printableRef}
        supplementalEditable={!readOnly}
        onSupplementalCommit={commitLiveSupplementalText}
        canvasTextAlignmentHandoff
      />
      </div>
      {resizeTooltipPortal}
      {editDialog}
    </div>
  );
}
